import { strToU8, unzip, unzipSync, zip, zipSync } from "fflate";

export interface MobileEnvelope {
  version: 1;
  algorithm: "AES-256-GCM";
  kdf: "PBKDF2-SHA256";
  iterations: number;
  salt: string;
  iv: string;
  ciphertext: string;
}

export interface MobileSegment {
  id: string;
  artifact_id: string;
  filename: string;
  unit_id: string;
  mid: string;
  ordinal: number;
  source_text: string;
  target: string;
  source_hash: string;
  locked: boolean;
  tag_signature: string[];
  review_status: string;
  match_origin?: string | null;
  match_percent?: number | null;
  /** Original spreadsheet row carried by SDLXLIFF cell context, when present. */
  reference_row_hint?: number | null;
  /** Unsegmented source cell text shared by every Studio segment in the unit. */
  unit_source_text?: string | null;
}

export interface MobileReferenceFile {
  id: string;
  name: string;
  mime_type: string;
  size: number;
  content_base64: string;
  /** Generated page-faithful PDF associated with an original Office file. */
  layout_preview_for?: string;
  generated_preview?: boolean;
}

export interface PortableDocxPreview {
  /** Sanitized semantic HTML produced locally from the original DOCX. */
  html: string;
  /** Plain text remains available for current-string search and fallback. */
  text: string;
  warnings: string[];
}

export interface MobileTmEntry {
  id: string;
  source: string;
  target: string;
  origin: string;
  changed_at?: string | null;
}

export interface MobileResourceSummary {
  id: string;
  name: string;
  format: string;
  selected: boolean;
  entry_count?: number | null;
  note?: string | null;
}

export type MobileBilingualFormat = "xliff" | "phrase_review_docx" | "phrase_joined_docx";

export interface MobileJobCatalogEntry {
  id: string;
  name: string;
  client: string;
  client_slug: string;
  identifier: string;
  period: string;
  folder: string;
  bundle_url: string;
  source_locale: string;
  target_locale: string;
  confirmed_words: number;
  total_words: number;
  status: "processing" | "review" | "complete" | string;
  updated_at: string;
}

export interface MobileJobBundle {
  version: 1;
  kind: "translation-command-center-mobile-job";
  created_at: string;
  source_package_hash: string;
  project: {
    id: string;
    name: string;
    profile: string;
    client: string;
    source_locale: string;
    target_locale: string;
    deadline?: string | null;
  };
  files: Array<{ artifact_id: string; name: string; sha256: string; size: number; content_base64: string; format?: MobileBilingualFormat }>;
  reference_files?: MobileReferenceFile[];
  /** Portable, job-scoped concordance. Reusable full client memories stay on the Codex/Mac side. */
  tm_entries?: MobileTmEntry[];
  glossary_entries?: Array<{ id: string; source_term: string; target_term: string; authority: string; status: string }>;
  resources?: {
    translation_memories: MobileResourceSummary[];
    glossaries: MobileResourceSummary[];
  };
  segments: MobileSegment[];
  companions: Record<string, {
    tm_matches: Array<{ id: string; score: number; source: string; target: string; origin: string }>;
    terms: Array<{ id: string; source_term: string; target_term?: string | null; authority: string; status: string }>;
    screenshots: Array<{ id: string; role: string; filename: string; image_url: string; ocr_text: string; location?: string | null; reference_file_id?: string | null }>;
    reference_row?: { filename?: string; location?: string | null; sheet?: string | null; row_number?: number | null; headers?: string[]; cells?: Array<{ column?: string; coordinate?: string; value?: string; language?: string }> } | null;
    documents?: Array<{ id: string; kind: string; label: string; location?: string | null; preview?: string; confidence?: number; document_url?: string }>;
    context: Record<string, unknown>;
  }>;
  qa_issues: MobileQaIssue[];
  qa_profile: Record<string, string[]>;
  checklists: Array<{ name: string; xml: string }>;
  delivery: Record<string, string>;
  import_warnings?: string[];
}

export interface MobileQaIssue {
  id?: string;
  segment_id: string;
  category: string;
  code: string;
  severity: "critical" | "major" | "minor" | string;
  message: string;
  source_value: string;
  target_value: string;
  filename: string;
  segment_number: number;
  match_percent?: number | null;
  match_origin?: string | null;
  review_status?: string;
  report_title?: string;
}

export interface MobilePatch {
  version: 1;
  project_id: string;
  source_package_hash: string;
  submitted_at: string;
  complete: boolean;
  changes: Array<{ segment_id: string; source_hash: string; target: string; review_status: string }>;
}

export interface MobileStorageStatus {
  supported: boolean;
  persisted: boolean | null;
  usage_bytes: number | null;
  quota_bytes: number | null;
  available_bytes: number | null;
  required_bytes: number;
  sufficient: boolean | null;
  pressure: "unknown" | "ok" | "low" | "insufficient";
}

type PortableStorageManager = Pick<StorageManager, "estimate" | "persist" | "persisted">;

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const TOKEN_RE = /⟦T(\d+)⟧/g;
const CLOSE_TOKEN_RE = /⟦\/T(\d+)⟧/g;
const ANY_TOKEN_RE = /⟦(?:\/?T|R)\d+⟧/g;
const CONFIRMED = new Set(["validated", "approved", "signed_off", "protected"]);
const BILINGUAL_EXTENSIONS = [".sdlxliff", ".xlf", ".xliff"];
const WORKBOOK_EXTENSIONS = [".xlsx", ".xlsm", ".xls", ".ods", ".csv", ".tsv"];
const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".gif", ".avif", ".heic", ".heif"];
const REFERENCE_EXTENSIONS = [
  ".pdf", ".doc", ".docx", ".rtf", ".odt", ".ppt", ".pptx", ".odp",
  ".txt", ".md", ".html", ".htm", ...WORKBOOK_EXTENSIONS, ...IMAGE_EXTENSIONS,
];
const TM_EXTENSIONS = [".tmx", ".sdltm"];
const GLOSSARY_EXTENSIONS = [".tbx", ".sdltb"];
const MAX_PORTABLE_ZIP_BYTES = 192 * 1024 * 1024;
const MAX_PORTABLE_ZIP_ENTRIES = 6_000;
const MAX_PORTABLE_EXPANDED_BYTES = 384 * 1024 * 1024;

type PortableResourceInput = { name: string; bytes: Uint8Array; kind: "tm" | "glossary" };
type PortableBilingualInput = { name: string; bytes: Uint8Array; format: MobileBilingualFormat };
export type MobileImportOptions = { workbookIntent?: "auto" | "terminology"; signal?: AbortSignal };

export function normalizeVivoOsApostrophes(value: string, profile: string, filename: string) {
  const vivoOs = profile.toLocaleLowerCase() === "vivo" && /(?:^|[_ .-])OS(?:[_ .-]|$)/.test(filename);
  return vivoOs ? value.replace(/(^|[^\\])'/g, "$1\\'") : value;
}

function portableBilingualFormat(name: string, bytes: Uint8Array): MobileBilingualFormat | null {
  const lower = name.toLocaleLowerCase();
  if (BILINGUAL_EXTENSIONS.some((extension) => lower.endsWith(extension))) return "xliff";
  if (!lower.endsWith(".docx")) return null;
  try {
    const documentXml = unzipSync(bytes)["word/document.xml"];
    if (!documentXml) return null;
    const documentNode = new DOMParser().parseFromString(decoder.decode(documentXml), "application/xml");
    if (documentNode.querySelector("parsererror")) return null;
    const headers = elements(documentNode, "tr").map((row) => directWordCells(row).map((cell) => normalizedHeader(wordCellText(cell))));
    if (headers.some((row) => row.includes("segmentid") && row.includes("segmentstatus") && row.includes("sourcesegment") && row.includes("targetsegment"))) return "phrase_review_docx";
    if (headers.some((row) => row.includes("id") && row.some((item) => item.startsWith("source")) && row.some((item) => item.startsWith("target")))) return "phrase_joined_docx";
  } catch {
    return null;
  }
  return null;
}

export async function importMobileJobFiles(files: File[], currentBundle?: MobileJobBundle | null, onProgress?: (message: string) => void, options: MobileImportOptions = {}): Promise<MobileJobBundle> {
  if (!files.length) throw new Error("Choose a mobile job ZIP, mobile-job.json, or one or more SDLXLIFF/XLIFF files.");
  throwIfImportAborted(options.signal);
  const bilinguals: PortableBilingualInput[] = [];
  const references: Array<{ name: string; bytes: Uint8Array }> = [];
  const resources: PortableResourceInput[] = [];
  const oversizedReferences: string[] = [];
  let suppliedScreenshotCount = 0;
  let bundledJob: MobileJobBundle | null = null;
  const intakeHint = files.map((file) => file.name).join(" ");
  for (const file of files) {
    throwIfImportAborted(options.signal);
    onProgress?.(`Reading ${file.name}…`);
    const lower = file.name.toLocaleLowerCase();
    if (lower.endsWith(".zip") && file.size > MAX_PORTABLE_ZIP_BYTES) {
      throw new Error(`${file.name} is larger than the ${MAX_PORTABLE_ZIP_BYTES / 1024 / 1024} MB compressed phone limit. Ask Codex to stage it as a compact served job.`);
    }
    if (!lower.endsWith(".zip") && !lower.endsWith(".docx") && REFERENCE_EXTENSIONS.some((extension) => lower.endsWith(extension)) && file.size > 25 * 1024 * 1024) {
      oversizedReferences.push(file.name);
      continue;
    }
    const bytes = await readFileBytes(file, options.signal);
    throwIfImportAborted(options.signal);
    if (lower.endsWith(".json")) {
      try { bundledJob = validateImportedBundle(JSON.parse(decoder.decode(bytes))); }
      catch (error) {
        if (error instanceof Error && error.message.includes("supported Translation Command Center")) throw error;
        throw new Error(`${file.name} is not valid mobile-job JSON.`);
      }
      continue;
    }
    if (lower.endsWith(".zip")) {
      const archivePlan = portableZipPreflight(bytes, file.name);
      const bundledNames = archivePlan.filter((entry) => entry.name.toLocaleLowerCase().endsWith("mobile-job.json"));
      if (bundledNames.length > 1) throw new Error(`${file.name} contains multiple mobile-job.json files. Use one complete job package at a time.`);
      if (bundledNames.length === 1) {
        onProgress?.(`Opening the prepared phone job in ${file.name}…`);
        const bundledEntries = await unzipPortableArchive(bytes, file.name, (entry) => entry.name.toLocaleLowerCase().endsWith("mobile-job.json"), options.signal);
        const bundled = Object.entries(bundledEntries).find(([name]) => name.toLocaleLowerCase().endsWith("mobile-job.json"));
        if (!bundled) throw new Error(`${file.name} contains a damaged mobile-job.json entry.`);
        try { bundledJob = validateImportedBundle(JSON.parse(decoder.decode(bundled[1]))); }
        catch (error) {
          if (error instanceof Error && error.message.includes("supported Translation Command Center")) throw error;
          throw new Error(`${file.name} contains an invalid mobile-job.json.`);
        }
        // A mobile-job.json is self-contained by contract. Avoid inflating the
        // duplicate delivery tree, screenshots and Office files packaged next
        // to it; on iPhone that otherwise multiplies peak memory for no gain.
        continue;
      }

      const archiveImages = archivePlan.filter((entry) => isPortableImageName(entry.name));
      suppliedScreenshotCount += archiveImages.length;
      for (const image of archiveImages) if (image.originalSize > 25 * 1024 * 1024) oversizedReferences.push(image.name);
      onProgress?.(`Expanding bilinguals and searchable references in ${file.name}…`);
      const entries = await unzipPortableArchive(bytes, file.name, (entry) => {
        if (isPortableImageName(entry.name)) return false;
        if (!isPortableIntakeName(entry.name)) return false;
        if (isPortableReferenceName(entry.name) && !isPortableBilingualName(entry.name) && entry.originalSize > 25 * 1024 * 1024) {
          oversizedReferences.push(entry.name);
          return false;
        }
        return true;
      }, options.signal);
      const archiveReferenceInputs: Array<{ name: string; bytes: Uint8Array }> = [];
      for (const [name, entry] of Object.entries(entries)) {
        const bilingualFormat = portableBilingualFormat(name, entry);
        if (bilingualFormat) {
          bilinguals.push({ name: portableZipPath(name), bytes: entry, format: bilingualFormat });
        } else if (TM_EXTENSIONS.some((extension) => name.toLocaleLowerCase().endsWith(extension))) {
          resources.push({ name: portableZipPath(name), bytes: entry, kind: "tm" });
        } else if (GLOSSARY_EXTENSIONS.some((extension) => name.toLocaleLowerCase().endsWith(extension))) {
          resources.push({ name: portableZipPath(name), bytes: entry, kind: "glossary" });
        } else if (REFERENCE_EXTENSIONS.some((extension) => name.toLocaleLowerCase().endsWith(extension))) {
          if (entry.byteLength <= 25 * 1024 * 1024) {
            const reference = { name: portableZipPath(name), bytes: entry };
            references.push(reference);
            archiveReferenceInputs.push(reference);
          }
          else oversizedReferences.push(name);
        }
      }

      const selectableImages = archiveImages.filter((entry) => entry.originalSize <= 25 * 1024 * 1024);
      if (selectableImages.length) {
        const requiredImages = selectableImages.length <= 12
          ? new Set(selectableImages.map((entry) => basename(entry.name).toLocaleLowerCase()))
          : await portableImageNamesRequiredByReferences(archiveReferenceInputs, currentBundle, options.signal);
        if (requiredImages.size) {
          onProgress?.(`Linking ${requiredImages.size} exact screenshot${requiredImages.size === 1 ? "" : "s"} without loading unused images…`);
          const imageEntries = await unzipPortableArchive(bytes, file.name, (entry) => isPortableImageName(entry.name) && requiredImages.has(basename(entry.name).toLocaleLowerCase()), options.signal);
          for (const [name, entry] of Object.entries(imageEntries)) references.push({ name: portableZipPath(name), bytes: entry });
        }
      }
      continue;
    }
    const bilingualFormat = portableBilingualFormat(file.name, bytes);
    if (bilingualFormat) bilinguals.push({ name: file.name, bytes, format: bilingualFormat });
    else if (TM_EXTENSIONS.some((extension) => lower.endsWith(extension))) resources.push({ name: file.name, bytes, kind: "tm" });
    else if (GLOSSARY_EXTENSIONS.some((extension) => lower.endsWith(extension))) resources.push({ name: file.name, bytes, kind: "glossary" });
    else if (REFERENCE_EXTENSIONS.some((extension) => lower.endsWith(extension))) {
      if (bytes.byteLength <= 25 * 1024 * 1024) references.push({ name: file.name, bytes });
      else oversizedReferences.push(file.name);
    }
  }
  throwIfImportAborted(options.signal);
  const skippedOversizedReferences = [...new Set(oversizedReferences)];
  onProgress?.("Building the phone bilingual…");
  await portableImportYield(options.signal);
  let result: MobileJobBundle;
  if (bundledJob) result = await attachReferenceFiles(bundledJob, references, options.signal);
  else if (!bilinguals.length && currentBundle && (references.length || resources.length)) result = await attachReferenceFiles(currentBundle, references, options.signal);
  else if (bilinguals.length) result = await buildImportedMobileBundle(bilinguals, references, portableLinkedImageNames(currentBundle), intakeHint, options.signal);
  else {
    if (skippedOversizedReferences.length) throw new Error(`${skippedOversizedReferences[0]} is larger than the 25 MB per-reference phone limit. Add a smaller copy or include it in the Mac-generated companion package.`);
    throw new Error(references.length
      ? "These references are ready, but no open bilingual job is available to attach them to. Open the SDLXLIFF/XLIFF first."
      : "No supported SDLXLIFF/XLIFF job was found in the selected files.");
  }
  if (currentBundle && bilinguals.length) result = mergePortableCompanionEvidence(result, currentBundle);
  throwIfImportAborted(options.signal);
  result = await mapPortableReferenceEvidence(result, options.signal);
  throwIfImportAborted(options.signal);
  result = await attachPortableResources(result, resources, options.signal);
  const confirmedGlossaryIds = new Set<string>();
  if (options.workbookIntent === "terminology") {
    for (const reference of references) {
      if (!WORKBOOK_EXTENSIONS.some((extension) => reference.name.toLocaleLowerCase().endsWith(extension))) continue;
      confirmedGlossaryIds.add(`phone-reference-${(await sha256Hex(reference.bytes)).slice(0, 20)}`);
    }
  }
  result = await attachPortableGlossaryEvidence(result, confirmedGlossaryIds, options.signal);
  throwIfImportAborted(options.signal);
  result = compactPortableScreenshotStorage(result);
  const importWarnings: string[] = [];
  if (skippedOversizedReferences.length) importWarnings.push(`${skippedOversizedReferences.length} reference file${skippedOversizedReferences.length === 1 ? " was" : "s were"} skipped because the per-file phone limit is 25 MB.`);
  const suppliedScreenshots = suppliedScreenshotCount || references.filter((item) => IMAGE_EXTENSIONS.some((extension) => item.name.toLocaleLowerCase().endsWith(extension))).length;
  const linkedScreenshots = Object.values(result.companions).reduce((total, companion) => total + companion.screenshots.length, 0);
  if (bilinguals.length && suppliedScreenshots && !linkedScreenshots) {
    importWarnings.push(`${suppliedScreenshots} screenshot image${suppliedScreenshots === 1 ? " was" : "s were"} supplied, but this raw package contains no deterministic row-to-image link. Exact screenshots require a Codex-served companion map; the bilinguals and exact workbook rows remain usable.`);
  }
  onProgress?.("Saving the prepared phone job…");
  return { ...result, import_warnings: importWarnings.length ? importWarnings : undefined };
}

function unzipPortableArchive(bytes: Uint8Array, filename: string, filter?: (entry: { name: string; originalSize: number; size: number; compression: number }) => boolean, signal?: AbortSignal): Promise<Record<string, Uint8Array>> {
  // fflate's asynchronous path performs the expensive inflate work outside
  // Safari's main interaction loop. Small archives stay synchronous to avoid
  // worker startup overhead during ordinary single-file imports and tests.
  if (bytes.byteLength < 8 * 1024 * 1024) {
    try {
      throwIfImportAborted(signal);
      const entries = unzipSync(bytes, filter ? { filter } : undefined);
      throwIfImportAborted(signal);
      return Promise.resolve(entries);
    }
    catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return Promise.reject(error);
      return Promise.reject(new Error(`${filename} is not a readable ZIP package.`));
    }
  }
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (error: Error | null, entries?: Record<string, Uint8Array>) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", abort);
      if (error) reject(error);
      else resolve(entries ?? {});
    };
    const terminate = unzip(bytes, filter ? { filter } : {}, (error, entries) => finish(error ? new Error(`${filename} is not a readable ZIP package.`) : null, entries));
    const abort = () => {
      terminate();
      finish(new DOMException("Import cancelled.", "AbortError"));
    };
    if (signal?.aborted) abort();
    else signal?.addEventListener("abort", abort, { once: true });
  });
}

function throwIfImportAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException("Import cancelled.", "AbortError");
}

async function portableImportYield(signal?: AbortSignal) {
  throwIfImportAborted(signal);
  if (!signal) return;
  await new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 0);
  });
  throwIfImportAborted(signal);
}

/**
 * Read only ZIP central-directory metadata before inflating any entry. This
 * makes the advertised entry count and expanded size cheap to reject on an
 * iPhone instead of discovering a ZIP bomb after allocating its output.
 */
type PortableZipEntryPlan = { name: string; originalSize: number; compressedSize: number };

function portableZipPreflight(bytes: Uint8Array, filename: string): PortableZipEntryPlan[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const minimumEocd = 22;
  if (bytes.byteLength < minimumEocd) throw new Error(`${filename} is not a readable ZIP package.`);
  const searchStart = Math.max(0, bytes.byteLength - 65_557);
  let eocd = -1;
  for (let offset = bytes.byteLength - minimumEocd; offset >= searchStart; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) { eocd = offset; break; }
  }
  if (eocd < 0) throw new Error(`${filename} is not a readable ZIP package.`);
  const entryCount = view.getUint16(eocd + 10, true);
  const directorySize = view.getUint32(eocd + 12, true);
  const directoryOffset = view.getUint32(eocd + 16, true);
  if (entryCount === 0xffff || directorySize === 0xffffffff || directoryOffset === 0xffffffff) {
    throw new Error(`${filename} uses ZIP64 and is too large for direct phone preparation. Ask Codex to stage it as a compact served job.`);
  }
  if (entryCount > MAX_PORTABLE_ZIP_ENTRIES || directoryOffset + directorySize > eocd || directoryOffset + directorySize > bytes.byteLength) {
    throw new Error(`${filename} expands beyond the phone safety limit. Ask Codex to stage it as a compact served job.`);
  }
  let offset = directoryOffset;
  let expandedBytes = 0;
  const entries: PortableZipEntryPlan[] = [];
  for (let index = 0; index < entryCount; index += 1) {
    if (offset + 46 > bytes.byteLength || view.getUint32(offset, true) !== 0x02014b50) throw new Error(`${filename} has a damaged ZIP directory.`);
    const flags = view.getUint16(offset + 8, true);
    if (flags & 1) throw new Error(`${filename} is password-protected. Remove the ZIP password or ask Codex to stage it.`);
    const compressedSize = view.getUint32(offset + 20, true);
    const originalSize = view.getUint32(offset + 24, true);
    expandedBytes += originalSize;
    if (expandedBytes > MAX_PORTABLE_EXPANDED_BYTES) throw new Error(`${filename} expands beyond the phone safety limit. Ask Codex to stage it as a compact served job.`);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
    entries.push({ name: portableZipPath(name), originalSize, compressedSize });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  if (offset > directoryOffset + directorySize) throw new Error(`${filename} has a damaged ZIP directory.`);
  return entries;
}

function isPortableImageName(name: string) {
  const lower = name.toLocaleLowerCase();
  return IMAGE_EXTENSIONS.some((extension) => lower.endsWith(extension));
}

function isPortableBilingualName(name: string) {
  const lower = name.toLocaleLowerCase();
  return BILINGUAL_EXTENSIONS.some((extension) => lower.endsWith(extension)) || lower.endsWith(".docx");
}

function isPortableReferenceName(name: string) {
  const lower = name.toLocaleLowerCase();
  return REFERENCE_EXTENSIONS.some((extension) => lower.endsWith(extension));
}

function isPortableIntakeName(name: string) {
  const lower = name.toLocaleLowerCase();
  return isPortableBilingualName(name)
    || TM_EXTENSIONS.some((extension) => lower.endsWith(extension))
    || GLOSSARY_EXTENSIONS.some((extension) => lower.endsWith(extension))
    || isPortableReferenceName(name);
}

type PortableWorkbookRow = {
  workbook: MobileReferenceFile;
  sheet: string;
  headerRow: number;
  rowNumber: number;
  headers: string[];
  cells: Array<{ column: string; coordinate: string; value: string }>;
};

export type PortableWorkbookGrid = {
  sheets: Array<{
    name: string;
    startRow: number;
    startColumn: number;
    columnCount: number;
    rows: Array<{
      rowNumber: number;
      cells: Array<{ columnIndex: number; coordinate: string; value: string }>;
    }>;
  }>;
  truncated: boolean;
};

/**
 * Rebuild the deterministic part of Screenshot Companion in the browser.
 * vivo source files and reference sheets share both a translation-order token
 * (for example NewTranslate_TR20260825008) and row order.  We still require an
 * exact English cell match before attaching a row, so unrelated rows never
 * become plausible-looking context merely because they are nearby.
 */
async function mapPortableReferenceEvidence(bundle: MobileJobBundle, signal?: AbortSignal): Promise<MobileJobBundle> {
  const references = bundle.reference_files ?? [];
  const workbooks = references.filter((item) => WORKBOOK_EXTENSIONS.some((extension) => item.name.toLocaleLowerCase().endsWith(extension)));
  if (!workbooks.length) return bundle;
  const rows: PortableWorkbookRow[] = [];
  for (const workbook of workbooks) {
    rows.push(...await readPortableWorkbookRows(workbook, signal));
    await portableImportYield(signal);
  }
  if (!rows.length) return bundle;
  const imagesByKey = new Map<string, MobileReferenceFile>();
  for (const reference of references) {
    if (!reference.mime_type.startsWith("image/")) continue;
    const name = basename(reference.name).toLocaleLowerCase();
    imagesByKey.set(name, reference);
    imagesByKey.set(name.replace(/\.[^.]+$/, ""), reference);
  }
  const companions: MobileJobBundle["companions"] = { ...bundle.companions };
  for (const [segmentIndex, segment] of bundle.segments.entries()) {
    if (segmentIndex % 24 === 0) await portableImportYield(signal);
    const token = referenceToken(segment.filename);
    const eligible = rows.filter((row) => !token || referenceToken(`${row.workbook.name} ${row.sheet}`) === token);
    if (!eligible.length) continue;
    const exact = eligible.filter((row) => rowMatchesSource(row, segment.unit_source_text || segment.source_text));
    if (!exact.length) continue;
    const expectedRowNumber = segment.reference_row_hint ?? (exact[0].headerRow + segment.ordinal);
    const expectedRow = exact.find((row) => row.rowNumber === expectedRowNumber);
    const chosen = expectedRow ?? exact.sort((left, right) => {
      const leftDistance = Math.abs(left.rowNumber - expectedRowNumber);
      const rightDistance = Math.abs(right.rowNumber - expectedRowNumber);
      return leftDistance - rightDistance || left.rowNumber - right.rowNumber;
    })[0];
    const current = companions[segment.id] ?? { tm_matches: [], terms: [], screenshots: [], context: {} };
    const linked = linkedPortableScreenshots(chosen, imagesByKey);
    const screenshotKeys = new Set(current.screenshots.map((item) => `${item.role}\u0000${item.filename.toLocaleLowerCase()}`));
    const screenshots = [...current.screenshots];
    for (const screenshot of linked) {
      const key = `${screenshot.role}\u0000${screenshot.filename.toLocaleLowerCase()}`;
      if (!screenshotKeys.has(key)) {
        screenshotKeys.add(key);
        screenshots.push(screenshot);
      }
    }
    companions[segment.id] = {
      ...current,
      screenshots,
      reference_row: {
        filename: chosen.workbook.name,
        location: `sheet:${chosen.sheet}:row:${chosen.rowNumber}`,
        sheet: chosen.sheet,
        row_number: chosen.rowNumber,
        headers: chosen.headers,
        cells: chosen.cells,
      },
    };
  }
  return { ...bundle, companions };
}

/**
 * Reuse uploaded image payloads by filename instead of retaining another data
 * URL in every inherited companion. Large ZIPs also drop unlinked job-wide
 * images that the phone cannot map or present usefully; small manual image
 * selections remain available for attachment by the active editor.
 */
function compactPortableScreenshotStorage(bundle: MobileJobBundle): MobileJobBundle {
  const references = bundle.reference_files ?? [];
  const images = references.filter((item) => item.mime_type.startsWith("image/"));
  if (!images.length) return bundle;
  const candidates = new Map<string, MobileReferenceFile[]>();
  for (const image of images) {
    const key = basename(image.name).toLocaleLowerCase();
    candidates.set(key, [...(candidates.get(key) ?? []), image]);
  }
  const referencedIds = new Set<string>();
  const companions: MobileJobBundle["companions"] = {};
  for (const [segmentId, companion] of Object.entries(bundle.companions)) {
    const screenshots = companion.screenshots.map((screenshot) => {
      const existing = screenshot.reference_file_id && references.find((item) => item.id === screenshot.reference_file_id);
      const matches = candidates.get(basename(screenshot.filename).toLocaleLowerCase()) ?? [];
      const reference = existing ?? (matches.length === 1 ? matches[0] : null);
      if (!reference) return screenshot;
      referencedIds.add(reference.id);
      return { ...screenshot, image_url: "", reference_file_id: reference.id };
    });
    companions[segmentId] = { ...companion, screenshots };
  }
  const retained = images.length > 12
    ? references.filter((item) => !item.mime_type.startsWith("image/") || referencedIds.has(item.id))
    : references;
  return { ...bundle, reference_files: retained, companions };
}

/**
 * A Mac-generated package already contains OCR-backed screenshot links, TM
 * results, terminology and document previews.  Reopening the same SDLXLIFF on
 * the phone must not discard that evidence merely because the bilingual bytes
 * changed after a legitimate save.  Identity is deliberately strict: file
 * order token, trans-unit id, segment id and source text all have to agree.
 */
function mergePortableCompanionEvidence(imported: MobileJobBundle, current: MobileJobBundle): MobileJobBundle {
  const currentByIdentity = new Map<string, MobileJobBundle["segments"][number]>();
  for (const segment of current.segments) currentByIdentity.set(portableSegmentIdentity(segment), segment);
  const companions = { ...imported.companions };
  let matchedSegments = 0;
  for (const segment of imported.segments) {
    const previousSegment = currentByIdentity.get(portableSegmentIdentity(segment));
    if (!previousSegment) continue;
    matchedSegments += 1;
    const previous = current.companions[previousSegment.id];
    if (!previous) continue;
    const own = companions[segment.id] ?? { tm_matches: [], terms: [], screenshots: [], context: {} };
    companions[segment.id] = {
      ...own,
      tm_matches: previous.tm_matches ?? own.tm_matches,
      terms: previous.terms ?? own.terms,
      screenshots: previous.screenshots ?? own.screenshots,
      documents: previous.documents ?? own.documents,
      context: { ...(previous.context ?? {}), ...(own.context ?? {}) },
    };
  }
  const sameJob = matchedSegments >= Math.max(1, Math.ceil(imported.segments.length * .7));
  return {
    ...imported,
    project: sameJob ? {
      ...imported.project,
      name: current.project.name,
      profile: current.project.profile,
      client: current.project.client,
    } : imported.project,
    companions,
  };
}

function portableSegmentIdentity(segment: MobileSegment) {
  return [
    referenceToken(segment.filename),
    segment.unit_id,
    segment.mid,
    normalizedReferenceText(segment.source_text),
  ].join("\u0000");
}

async function readPortableWorkbookRows(workbookFile: MobileReferenceFile, signal?: AbortSignal): Promise<PortableWorkbookRow[]> {
  const lower = workbookFile.name.toLocaleLowerCase();
  const bytes = fromBase64(workbookFile.content_base64);
  await portableImportYield(signal);
  const imported = await import("xlsx-js-style");
  const XLSX = imported.default ?? imported;
  let workbook: ReturnType<typeof XLSX.read>;
  if (lower.endsWith(".csv") || lower.endsWith(".tsv")) {
    workbook = XLSX.read(decoder.decode(bytes), { type: "string", raw: false, FS: lower.endsWith(".tsv") ? "\t" : "," });
  } else workbook = XLSX.read(bytes, { type: "array", raw: false, cellFormula: true, cellText: true, cellStyles: true });
  await portableImportYield(signal);
  const output: PortableWorkbookRow[] = [];
  for (const [sheetIndex, sheetName] of workbook.SheetNames.entries()) {
    if (sheetIndex) await portableImportYield(signal);
    const sheet = workbook.Sheets[sheetName] as Record<string, unknown>;
    const rangeText = typeof sheet["!ref"] === "string" ? sheet["!ref"] as string : "";
    if (!rangeText) continue;
    const range = XLSX.utils.decode_range(rangeText);
    const headerIndex = detectWorkbookHeaderRow(XLSX, sheet, range);
    const headers: string[] = [];
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      headers.push(portableCellValue(XLSX, sheet, headerIndex, column) || `Column ${column - range.s.c + 1}`);
    }
    for (let row = headerIndex + 1; row <= range.e.r; row += 1) {
      const cells: PortableWorkbookRow["cells"] = [];
      for (let column = range.s.c; column <= range.e.c; column += 1) {
        const value = portableCellValue(XLSX, sheet, row, column);
        if (!value.trim()) continue;
        cells.push({
          column: headers[column - range.s.c] ?? `Column ${column - range.s.c + 1}`,
          coordinate: XLSX.utils.encode_cell({ r: row, c: column }),
          value,
        });
      }
      if (cells.length) output.push({ workbook: workbookFile, sheet: sheetName, headerRow: headerIndex + 1, rowNumber: row + 1, headers, cells });
    }
  }
  return output;
}

/**
 * Decode a workbook into a sparse, visual grid for the phone reference pane.
 * This is display-only: real sheet names, coordinates and displayed values are
 * retained without changing the workbook or the bilingual. A generous guard
 * prevents a pathological workbook from exhausting mobile Safari's memory.
 */
export async function extractPortableWorkbookGrid(reference: MobileReferenceFile): Promise<PortableWorkbookGrid | null> {
  const lower = reference.name.toLocaleLowerCase();
  if (!WORKBOOK_EXTENSIONS.some((extension) => lower.endsWith(extension))) return null;
  try {
    const bytes = fromBase64(reference.content_base64);
    const imported = await import("xlsx-js-style");
    const XLSX = imported.default ?? imported;
    const workbook = lower.endsWith(".csv") || lower.endsWith(".tsv")
      ? XLSX.read(decoder.decode(bytes), { type: "string", raw: false, FS: lower.endsWith(".tsv") ? "\t" : "," })
      : XLSX.read(bytes, { type: "array", raw: false, cellFormula: true, cellText: true, cellStyles: true });
    const sheets: PortableWorkbookGrid["sheets"] = [];
    let retainedCells = 0;
    let truncated = false;
    const maxCells = 120_000;
    const maxColumns = 80;
    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName] as Record<string, unknown>;
      const rangeText = typeof sheet["!ref"] === "string" ? sheet["!ref"] as string : "";
      if (!rangeText) continue;
      const range = XLSX.utils.decode_range(rangeText);
      const endColumn = Math.min(range.e.c, range.s.c + maxColumns - 1);
      if (endColumn < range.e.c) truncated = true;
      const rows: PortableWorkbookGrid["sheets"][number]["rows"] = [];
      for (let row = range.s.r; row <= range.e.r; row += 1) {
        const cells: PortableWorkbookGrid["sheets"][number]["rows"][number]["cells"] = [];
        for (let column = range.s.c; column <= endColumn; column += 1) {
          const value = portableCellValue(XLSX, sheet, row, column);
          if (!value) continue;
          cells.push({ columnIndex: column, coordinate: XLSX.utils.encode_cell({ r: row, c: column }), value });
          retainedCells += 1;
          if (retainedCells >= maxCells) { truncated = true; break; }
        }
        if (cells.length) rows.push({ rowNumber: row + 1, cells });
        if (retainedCells >= maxCells) break;
      }
      sheets.push({ name: sheetName, startRow: range.s.r + 1, startColumn: range.s.c, columnCount: endColumn - range.s.c + 1, rows });
      if (retainedCells >= maxCells) break;
    }
    return sheets.length ? { sheets, truncated } : null;
  } catch {
    return null;
  }
}

function detectWorkbookHeaderRow(
  XLSX: typeof import("xlsx-js-style"),
  sheet: Record<string, unknown>,
  range: ReturnType<typeof XLSX.utils.decode_range>,
) {
  let bestRow = range.s.r;
  let bestScore = -1;
  for (let row = range.s.r; row <= Math.min(range.e.r, range.s.r + 12); row += 1) {
    let score = 0;
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const label = portableCellValue(XLSX, sheet, row, column);
      const key = normalizedHeader(label);
      if (isEnglishReferenceHeader(label) || isFrenchReferenceHeader(label)) score += 2;
      else if (["vzhrcn", "screenshot", "stringid", "requireid", "revisereason"].includes(key)) score += 2;
      else if (["comment", "stringdescription", "context", "definition", "status"].includes(key)) score += 1;
    }
    if (score > bestScore) { bestRow = row; bestScore = score; }
  }
  return bestRow;
}

function portableCellValue(
  XLSX: typeof import("xlsx-js-style"),
  sheet: Record<string, unknown>,
  row: number,
  column: number,
) {
  const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })] as { v?: unknown; w?: string; f?: string; l?: { Target?: string } } | undefined;
  if (!cell) return "";
  const rawValue = cell.v == null ? "" : String(cell.v);
  // vivo hides screenshot filenames with an empty display format in some
  // reference workbooks. Keep the visible formatted value when it exists, but
  // fall back to the underlying cell value when `w` is deliberately blank so
  // exact row-to-image linkage still works on the phone.
  const displayed = cell.w?.trim() ? cell.w : rawValue;
  const formula = cell.f ? `=${cell.f}` : "";
  const hyperlink = cell.l?.Target ?? "";
  const imageNames = imageNamesIn(`${displayed}\n${formula}\n${hyperlink}`);
  return imageNames.length ? imageNames.join("\n") : displayed || formula || hyperlink;
}

function rowMatchesSource(row: PortableWorkbookRow, source: string) {
  const wanted = normalizedReferenceText(source);
  if (!wanted) return false;
  return row.cells.some((cell) => isEnglishReferenceHeader(cell.column) && normalizedReferenceText(cell.value) === wanted);
}

function isEnglishReferenceHeader(header: string) {
  const key = normalizedHeader(header);
  return ["vus", "newvus", "source", "sourcetext", "sourceterm", "english", "englishterm", "enus", "valuesen"].includes(key)
    || key.startsWith("newvus") || key.startsWith("english") || key.startsWith("sourceterm");
}

function linkedPortableScreenshots(row: PortableWorkbookRow, imagesByKey: Map<string, MobileReferenceFile>) {
  const result: MobileJobBundle["companions"][string]["screenshots"] = [];
  const seen = new Set<string>();
  for (const cell of row.cells) {
    for (const name of imageNamesIn(cell.value)) {
      const key = basename(name).toLocaleLowerCase();
      const reference = imagesByKey.get(key) ?? imagesByKey.get(key.replace(/\.[^.]+$/, ""));
      if (!reference || seen.has(reference.id)) continue;
      seen.add(reference.id);
      result.push({
        id: `phone-linked-${reference.id}-${normalizedHeader(cell.column) || "context"}`,
        role: screenshotRole(cell.column),
        filename: basename(reference.name),
        // Keep one encoded copy in reference_files. Duplicating a multi-MB
        // image in every companion can push mobile Safari over its memory or
        // IndexedDB quota during a real vivo ZIP import.
        image_url: "",
        reference_file_id: reference.id,
        ocr_text: "",
        location: `sheet:${row.sheet}:row:${row.rowNumber}:${cell.coordinate}`,
      });
    }
  }
  return result;
}

function screenshotRole(header: string) {
  const key = normalizedHeader(header);
  if (/target|vfr|french|valuesfr/.test(key)) return "target";
  if (/source|vus|english|valuesen/.test(key)) return "source";
  if (/vzh|chinese|zhcn/.test(key)) return "chinese";
  return "context";
}

function referenceToken(value: string) {
  const match = value.toLocaleLowerCase().match(/(newtranslate|retranslate)[^a-z0-9]*(tr\d+)/i);
  return match ? `${match[1].toLocaleLowerCase()}_${match[2].toLocaleLowerCase()}` : "";
}

function normalizedHeader(value: string) {
  return value.normalize("NFKC").toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}

function normalizedReferenceText(value: string) {
  return value
    .replace(ANY_TOKEN_RE, "")
    .replaceAll("\\'", "'")
    .normalize("NFKC")
    .replace(/\s+/gu, " ")
    .trim()
    .toLocaleLowerCase();
}

function imageNamesIn(value: string) {
  const matches = value.match(/[^\s"'()=,;<>]+?\.(?:png|jpe?g|webp|gif|avif|heic|heif)/giu) ?? [];
  return [...new Set(matches.map((item) => basename(item.replaceAll("\\", "/"))))];
}

function basename(value: string) {
  return value.replaceAll("\\", "/").split("/").filter(Boolean).at(-1) ?? value;
}

async function attachReferenceFiles(bundle: MobileJobBundle, inputs: Array<{ name: string; bytes: Uint8Array }>, signal?: AbortSignal): Promise<MobileJobBundle> {
  if (!inputs.length) return bundle;
  const added = await buildReferenceFiles(inputs, portableLinkedImageNames(bundle), signal);
  const existing = bundle.reference_files ?? [];
  const seen = new Set(existing.map((item) => `${item.size}\u0000${item.content_base64.slice(0, 160)}`));
  return {
    ...bundle,
    reference_files: [...existing, ...added.filter((item) => {
      const key = `${item.size}\u0000${item.content_base64.slice(0, 160)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })],
  };
}

async function buildReferenceFiles(inputs: Array<{ name: string; bytes: Uint8Array }>, linkedImageNames = new Set<string>(), signal?: AbortSignal): Promise<MobileReferenceFile[]> {
  const images = inputs.filter((item) => IMAGE_EXTENSIONS.some((extension) => item.name.toLocaleLowerCase().endsWith(extension)));
  const nonImages = inputs.filter((item) => !images.includes(item));
  const output: MobileReferenceFile[] = [];
  // Workbooks and documents are encoded first. When a package contains dozens
  // of screenshots, their exact filenames can then be read from the workbook
  // before Safari allocates a base64 string for every image in the ZIP.
  for (const reference of nonImages) {
    await portableImportYield(signal);
    output.push(await buildReferenceFile(reference));
  }
  let selectedImages = images;
  if (images.length > 12) {
    const required = new Set([...linkedImageNames].map((name) => basename(name).toLocaleLowerCase()));
    // Read screenshot filenames straight from the original Office package as
    // well as from the normalized sheet model below. This avoids dropping
    // ordinary single-image cells while preparing a large vivo ZIP: some
    // workbook variants expose those strings differently through SheetJS,
    // whereas the underlying sharedStrings/worksheet XML is deterministic.
    for (const reference of nonImages) {
      if (!WORKBOOK_EXTENSIONS.some((extension) => reference.name.toLocaleLowerCase().endsWith(extension))) continue;
      for (const name of imageNamesInPortableWorkbookArchive(reference.bytes)) required.add(name);
    }
    const workbooks = output.filter((item) => WORKBOOK_EXTENSIONS.some((extension) => item.name.toLocaleLowerCase().endsWith(extension)));
    for (const workbook of workbooks) {
      for (const row of await readPortableWorkbookRows(workbook, signal)) {
        for (const cell of row.cells) {
          for (const name of imageNamesIn(cell.value)) required.add(basename(name).toLocaleLowerCase());
        }
      }
    }
    selectedImages = images.filter((item) => required.has(basename(item.name).toLocaleLowerCase()));
  }
  // Encode sequentially so the retained phone screenshots do not all allocate
  // a digest buffer and base64 string during the same microtask turn.
  for (const reference of selectedImages) {
    await portableImportYield(signal);
    output.push(await buildReferenceFile(reference));
  }
  return output;
}

function imageNamesInPortableWorkbookArchive(bytes: Uint8Array) {
  const names = new Set<string>();
  try {
    const archive = unzipSync(bytes);
    for (const [path, entry] of Object.entries(archive)) {
      if (!/\.(?:xml|rels)$/i.test(path)) continue;
      for (const name of imageNamesIn(decoder.decode(entry))) names.add(basename(name).toLocaleLowerCase());
    }
  } catch {
    // Legacy XLS/CSV inputs are not ZIP containers and continue through the
    // SheetJS row scan below.
  }
  return names;
}

async function portableImageNamesRequiredByReferences(
  inputs: Array<{ name: string; bytes: Uint8Array }>,
  currentBundle?: MobileJobBundle | null,
  signal?: AbortSignal,
) {
  const required = portableLinkedImageNames(currentBundle);
  const workbookInputs = inputs.filter((reference) => WORKBOOK_EXTENSIONS.some((extension) => reference.name.toLocaleLowerCase().endsWith(extension)));
  if (!workbookInputs.length) return required;
  await portableImportYield(signal);
  const imported = await import("xlsx-js-style");
  const XLSX = imported.default ?? imported;
  for (const reference of workbookInputs) {
    await portableImportYield(signal);
    const lower = reference.name.toLocaleLowerCase();
    for (const name of imageNamesInPortableWorkbookArchive(reference.bytes)) required.add(name);
    try {
      const workbook = lower.endsWith(".csv") || lower.endsWith(".tsv")
        ? XLSX.read(decoder.decode(reference.bytes), { type: "string", raw: false, FS: lower.endsWith(".tsv") ? "\t" : "," })
        : XLSX.read(reference.bytes, { type: "array", raw: false, cellFormula: true, cellText: true });
      await portableImportYield(signal);
      for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName] as Record<string, unknown>;
        for (const [coordinate, rawCell] of Object.entries(sheet)) {
          if (coordinate.startsWith("!") || !rawCell || typeof rawCell !== "object") continue;
          const cell = rawCell as { v?: unknown; w?: string; f?: string; l?: { Target?: string } };
          const evidence = `${cell.v ?? ""}\n${cell.w ?? ""}\n${cell.f ?? ""}\n${cell.l?.Target ?? ""}`;
          for (const name of imageNamesIn(evidence)) required.add(basename(name).toLocaleLowerCase());
        }
      }
    } catch {
      // A damaged/unsupported workbook will be reported by the normal
      // reference parser. It must not make unrelated image payloads inflate.
    }
  }
  return required;
}

async function buildReferenceFile(reference: { name: string; bytes: Uint8Array }): Promise<MobileReferenceFile> {
  const hash = await sha256Hex(reference.bytes);
  return {
    id: `phone-reference-${hash.slice(0, 20)}`,
    name: reference.name,
    mime_type: portableMimeType(reference.name),
    size: reference.bytes.byteLength,
    content_base64: bytesToBase64(reference.bytes),
  };
}

function portableLinkedImageNames(bundle?: MobileJobBundle | null) {
  const names = new Set<string>();
  if (!bundle) return names;
  for (const companion of Object.values(bundle.companions)) {
    for (const screenshot of companion.screenshots) names.add(basename(screenshot.filename).toLocaleLowerCase());
  }
  return names;
}

async function readFileBytes(file: File, signal?: AbortSignal): Promise<Uint8Array> {
  if (!signal && typeof file.arrayBuffer === "function") return new Uint8Array(await file.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    let settled = false;
    const cleanup = () => signal?.removeEventListener("abort", abort);
    const abort = () => {
      if (settled) return;
      settled = true;
      reader.abort();
      cleanup();
      reject(new DOMException("Import cancelled.", "AbortError"));
    };
    reader.onload = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(new Uint8Array(reader.result as ArrayBuffer));
    };
    reader.onerror = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(reader.error ?? new Error(`${file.name} could not be read.`));
    };
    if (signal?.aborted) { abort(); return; }
    signal?.addEventListener("abort", abort, { once: true });
    reader.readAsArrayBuffer(file);
  });
}

export async function saveImportedMobileBundle(bundle: MobileJobBundle, signal?: AbortSignal): Promise<void> {
  throwIfImportAborted(signal);
  const database = await mobileDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(["jobs", "preferences"], "readwrite");
      let settled = false;
      const cleanup = () => signal?.removeEventListener("abort", abort);
      const abort = () => {
        if (settled) return;
        try { transaction.abort(); } catch { /* Transaction may have committed between the tap and this task. */ }
      };
      const fail = () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(signal?.aborted ? new DOMException("Import cancelled.", "AbortError") : transaction.error ?? new Error("The imported job could not be saved on this phone."));
      };
      transaction.objectStore("jobs").put(bundle, "active");
      transaction.objectStore("preferences").put("imported", "active-job");
      transaction.oncomplete = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };
      transaction.onerror = fail;
      transaction.onabort = fail;
      if (signal?.aborted) abort();
      else signal?.addEventListener("abort", abort, { once: true });
    });
  } finally {
    database.close();
  }
}

export async function saveBundledMobileBundle(bundle: MobileJobBundle, branch = "bundled"): Promise<void> {
  return saveMobileBundle(bundle, `bundled:${branch}`);
}

async function saveMobileBundle(bundle: MobileJobBundle, key: string): Promise<void> {
  const database = await mobileDatabase();
  try {
    await putMobileDatabaseValue(database, "jobs", key, bundle, "The imported job could not be saved on this phone.");
  } finally {
    database.close();
  }
}

/**
 * Store the editable checkpoint separately from the immutable bilingual and
 * references. IndexedDB avoids localStorage's small per-origin ceiling on
 * large jobs; callers can still mirror a compact checkpoint synchronously as
 * a best-effort pagehide fallback.
 */
export async function saveMobilePatch(patch: MobilePatch, key: string): Promise<void> {
  const database = await mobileDatabase();
  try {
    await putMobileDatabaseValue(database, "patches", key, patch, "The latest edits could not be saved on this phone.");
  } finally {
    database.close();
  }
}

export async function loadMobilePatch(key: string): Promise<MobilePatch | null> {
  const database = await mobileDatabase();
  try {
    const value = await getMobileDatabaseValue(database, "patches", key, "The saved phone edits could not be opened.");
    return value ? validateMobilePatch(value) : null;
  } finally {
    database.close();
  }
}

export async function loadMobileActiveJobPreference(): Promise<"imported" | "served" | null> {
  const database = await mobileDatabase();
  try {
    const value = await getMobileDatabaseValue(database, "preferences", "active-job", "The phone job preference could not be opened.");
    return value === "imported" || value === "served" ? value : null;
  } finally {
    database.close();
  }
}

export async function saveMobileActiveJobPreference(value: "imported" | "served"): Promise<void> {
  const database = await mobileDatabase();
  try {
    await putMobileDatabaseValue(database, "preferences", "active-job", value, "The phone job preference could not be saved.");
  } finally {
    database.close();
  }
}

export async function loadImportedMobileBundle(): Promise<MobileJobBundle | null> {
  return loadMobileBundle("active");
}

export async function loadBundledMobileBundle(branch = "bundled"): Promise<MobileJobBundle | null> {
  const value = await loadMobileBundle(`bundled:${branch}`);
  return value ?? (branch === "bundled" ? loadMobileBundle("bundled") : null);
}

async function loadMobileBundle(key: string): Promise<MobileJobBundle | null> {
  const database = await mobileDatabase();
  try {
    const value = await getMobileDatabaseValue(database, "jobs", key, "The saved phone job could not be opened.");
    return value ? validateImportedBundle(value) : null;
  } finally {
    database.close();
  }
}

function mobileDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("translation-command-center-mobile", 3);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains("jobs")) request.result.createObjectStore("jobs");
      if (!request.result.objectStoreNames.contains("patches")) request.result.createObjectStore("patches");
      if (!request.result.objectStoreNames.contains("preferences")) request.result.createObjectStore("preferences");
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Private browser storage is unavailable."));
    request.onblocked = () => reject(new Error("Close other old TCC tabs, then try saving again."));
  });
}

function putMobileDatabaseValue(database: IDBDatabase, store: string, key: string, value: unknown, fallbackMessage: string) {
  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(store, "readwrite");
    let settled = false;
    const fail = () => {
      if (settled) return;
      settled = true;
      reject(transaction.error ?? new Error(fallbackMessage));
    };
    transaction.objectStore(store).put(value, key);
    transaction.oncomplete = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    transaction.onerror = fail;
    transaction.onabort = fail;
  });
}

function getMobileDatabaseValue(database: IDBDatabase, store: string, key: string, fallbackMessage: string) {
  return new Promise<unknown>((resolve, reject) => {
    const transaction = database.transaction(store, "readonly");
    const request = transaction.objectStore(store).get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? transaction.error ?? new Error(fallbackMessage));
    transaction.onabort = () => reject(transaction.error ?? new Error(fallbackMessage));
  });
}

function validateImportedBundle(value: unknown): MobileJobBundle {
  const bundle = value as MobileJobBundle;
  if (!bundle || bundle.version !== 1 || bundle.kind !== "translation-command-center-mobile-job" || !Array.isArray(bundle.files) || !Array.isArray(bundle.segments)) {
    throw new Error("This file is not a supported Translation Command Center mobile job.");
  }
  return bundle;
}

function validateMobilePatch(value: unknown): MobilePatch {
  const patch = value as MobilePatch;
  if (!patch || patch.version !== 1 || typeof patch.project_id !== "string" || typeof patch.source_package_hash !== "string" || !Array.isArray(patch.changes)) {
    throw new Error("The saved phone edits are not a supported TCC checkpoint.");
  }
  return patch;
}

/** Conservative structured-clone estimate without serializing another giant copy. */
export function estimateMobileBundleStorageBytes(bundle: MobileJobBundle): number {
  const seen = new WeakSet<object>();
  const walk = (value: unknown): number => {
    if (value == null) return 4;
    if (typeof value === "string") return value.length * 2 + 8;
    if (typeof value === "number" || typeof value === "bigint") return 16;
    if (typeof value === "boolean") return 8;
    if (typeof value !== "object") return 0;
    if (seen.has(value)) return 0;
    seen.add(value);
    if (value instanceof ArrayBuffer) return value.byteLength;
    if (ArrayBuffer.isView(value)) return value.byteLength;
    if (Array.isArray(value)) return 24 + value.reduce((total, item) => total + walk(item), 0);
    return 48 + Object.entries(value).reduce((total, [key, item]) => total + key.length * 2 + walk(item), 0);
  };
  // Structured-clone metadata and IndexedDB bookkeeping vary by WebKit build.
  // A 12.5% reserve keeps this useful as a warning without pretending it is an
  // exact quota contract.
  return Math.ceil(walk(bundle) * 1.125);
}

export async function getMobileStorageStatus(
  requiredBytes = 0,
  manager: Partial<PortableStorageManager> | undefined = typeof navigator === "undefined" ? undefined : navigator.storage,
): Promise<MobileStorageStatus> {
  if (!manager) return { supported: false, persisted: null, usage_bytes: null, quota_bytes: null, available_bytes: null, required_bytes: requiredBytes, sufficient: null, pressure: "unknown" };
  let persisted: boolean | null = null;
  let usage: number | null = null;
  let quota: number | null = null;
  try {
    if (manager.persisted) persisted = await manager.persisted.call(manager);
  } catch { /* Older/private WebKit can expose the method but reject it. */ }
  try {
    if (manager.estimate) {
      const estimate = await manager.estimate.call(manager);
      usage = Number.isFinite(estimate.usage) ? estimate.usage! : null;
      quota = Number.isFinite(estimate.quota) ? estimate.quota! : null;
    }
  } catch { /* The save path still handles the authoritative quota error. */ }
  const available = usage != null && quota != null ? Math.max(0, quota - usage) : null;
  const sufficient = available == null || requiredBytes <= 0 ? null : available >= requiredBytes;
  const lowWatermark = Math.max(requiredBytes * 1.35, 24 * 1024 * 1024);
  const pressure: MobileStorageStatus["pressure"] = available == null
    ? "unknown"
    : sufficient === false
      ? "insufficient"
      : available < lowWatermark
        ? "low"
        : "ok";
  return { supported: true, persisted, usage_bytes: usage, quota_bytes: quota, available_bytes: available, required_bytes: requiredBytes, sufficient, pressure };
}

export async function requestMobileStoragePersistence(
  manager: Partial<PortableStorageManager> | undefined = typeof navigator === "undefined" ? undefined : navigator.storage,
): Promise<boolean | null> {
  if (!manager) return null;
  try {
    if (manager.persisted && await manager.persisted.call(manager)) return true;
    if (manager.persist) return await manager.persist.call(manager);
  } catch { /* Unsupported/private WebKit remains best-effort storage. */ }
  return null;
}

export function isMobileQuotaError(error: unknown) {
  if (error instanceof DOMException && error.name === "QuotaExceededError") return true;
  return error instanceof Error && /quota|storage.*full|disk.*full/i.test(`${error.name} ${error.message}`);
}

async function buildImportedMobileBundle(inputs: PortableBilingualInput[], referenceInputs: Array<{ name: string; bytes: Uint8Array }> = [], linkedImageNames = new Set<string>(), intakeHint = "", signal?: AbortSignal): Promise<MobileJobBundle> {
  const files: MobileJobBundle["files"] = [];
  const segments: MobileSegment[] = [];
  const companions: MobileJobBundle["companions"] = {};
  const fileHashes: string[] = [];
  let sourceLocale = "en-US";
  let targetLocale = "fr-FR";
  let formatProfile = "";
  for (let fileIndex = 0; fileIndex < inputs.length; fileIndex += 1) {
    await portableImportYield(signal);
    const input = inputs[fileIndex];
    const fileHash = await sha256Hex(input.bytes);
    const artifactId = `phone-${fileIndex + 1}-${fileHash.slice(0, 12)}`;
    fileHashes.push(fileHash);
    if (input.format !== "xliff") {
      const parsed = await parsePortablePhraseDocx(input, artifactId);
      if (!parsed.segments.length) throw new Error(`${input.name} contains no safely editable Phrase bilingual rows.`);
      sourceLocale = parsed.sourceLocale || sourceLocale;
      targetLocale = parsed.targetLocale || targetLocale;
      formatProfile = "phrase";
      segments.push(...parsed.segments);
      for (const segment of parsed.segments) companions[segment.id] = { tm_matches: [], terms: [], screenshots: [], context: { phrase_format: input.format } };
      files.push({ artifact_id: artifactId, name: input.name, sha256: fileHash, size: input.bytes.byteLength, content_base64: bytesToBase64(input.bytes), format: input.format });
      continue;
    }
    const xml = decoder.decode(input.bytes);
    if (/neotrans:xliff:export/i.test(xml)) formatProfile = "gientech";
    const documentNode = new DOMParser().parseFromString(xml, "application/xml");
    if (documentNode.querySelector("parsererror")) throw new Error(`${input.name} is not valid XML.`);
    const fileElement = elements(documentNode, "file")[0];
    sourceLocale = fileElement?.getAttribute("source-language") || sourceLocale;
    targetLocale = fileElement?.getAttribute("target-language") || targetLocale;
    const contextRows = sourceCellRows(documentNode);
    let fileOrdinal = 0;
    for (const unit of elements(documentNode, "trans-unit")) {
      const sourceContainer = directChild(unit, "seg-source") ?? directChild(unit, "source");
      const targetContainer = directChild(unit, "target");
      if (!sourceContainer || !targetContainer) continue;
      const unitSourceContainer = directChild(unit, "source") ?? sourceContainer;
      const unitSourceText = tokenizedContent(unitSourceContainer);
      const referenceRowHint = sourceRowForUnit(unit, contextRows);
      const sourceMarks = elements(sourceContainer, "mrk").filter((item) => item.getAttribute("mtype") === "seg");
      const candidates = sourceMarks.length ? sourceMarks : [sourceContainer];
      for (const source of candidates) {
        if (fileOrdinal % 24 === 0) await portableImportYield(signal);
        const mid = source === sourceContainer ? "" : source.getAttribute("mid") ?? "";
        const target = mid
          ? elements(targetContainer, "mrk").find((item) => item.getAttribute("mtype") === "seg" && (item.getAttribute("mid") ?? "") === mid)
          : targetContainer;
        if (!target) throw new Error(`${input.name}: target mapping is missing for segment ${fileOrdinal + 1}.`);
        const sourceText = tokenizedContent(source);
        fileOrdinal += 1;
        const metadata = mid ? elements(documentNode, "seg").find((item) => item.getAttribute("id") === mid) : null;
        const segment: MobileSegment = {
          id: `${artifactId}-segment-${fileOrdinal}`,
          artifact_id: artifactId,
          filename: input.name,
          unit_id: unit.getAttribute("id") ?? String(fileOrdinal),
          mid,
          ordinal: fileOrdinal,
          source_text: sourceText,
          target: tokenizedContent(target),
          source_hash: await sha256Hex(encoder.encode(sourceText)),
          locked: unit.getAttribute("translate") === "no",
          tag_signature: sourceText.match(ANY_TOKEN_RE) ?? [],
          review_status: reviewStatusFor(metadata?.getAttribute("conf") ?? "Draft"),
          match_origin: "imported bilingual",
          match_percent: null,
          reference_row_hint: referenceRowHint,
          unit_source_text: unitSourceText,
        };
        segments.push(segment);
        companions[segment.id] = { tm_matches: [], terms: [], screenshots: [], context: {} };
      }
    }
    if (!fileOrdinal) throw new Error(`${input.name} contains no safely editable XLIFF segments.`);
    files.push({
      artifact_id: artifactId,
      name: input.name,
      sha256: fileHash,
      size: input.bytes.byteLength,
      content_base64: bytesToBase64(input.bytes),
      format: "xliff",
    });
  }
  const joinedName = commonJobName(inputs.map((item) => item.name.split("/").at(-1) || item.name));
  const packageNames = [...inputs.map((item) => item.name), ...referenceInputs.map((item) => item.name)].join(" ");
  const lowerName = `${joinedName} ${intakeHint} ${packageNames}`.toLocaleLowerCase();
  // vivo handoffs are frequently delivered as generically named archives
  // such as `French(14).zip`. Detect the client from the actual bilingual
  // naming contract instead of relying on the outer archive or a common
  // multi-file prefix (which can collapse to just `French`).
  const vivoBilingualSignature = inputs.some((item) => /(?:^|\/)French_(?:OS_)?(?:NewTranslate|ReTranslate)_TR\d+.*\.sdlxliff$/i.test(item.name));
  const profile = /huawei|digital[ _-]?power/.test(lowerName) ? "huawei_digital_power"
    : lowerName.includes("roborock") ? "roborock"
      : lowerName.includes("vivo") || vivoBilingualSignature || /(?:^|[_ .-])os(?:[_ .-]|$)/i.test(joinedName) ? "vivo"
        : formatProfile || "generic";
  const client = profile === "huawei_digital_power" ? "Huawei Digital Power" : profile === "gientech" ? "GienTech" : profile === "phrase" ? "Phrase / Memsource" : profile === "generic" ? "Imported job" : profile;
  return {
    version: 1,
    kind: "translation-command-center-mobile-job",
    created_at: new Date().toISOString(),
    source_package_hash: await sha256Hex(encoder.encode(fileHashes.join("\n"))),
    project: {
      id: `phone-import-${(await sha256Hex(encoder.encode(fileHashes.join("|")))).slice(0, 20)}`,
      name: joinedName,
      profile,
      client,
      source_locale: sourceLocale,
      target_locale: targetLocale,
    },
    files,
    reference_files: await buildReferenceFiles(referenceInputs, linkedImageNames, signal),
    segments,
    companions,
    qa_issues: [],
    qa_profile: {},
    checklists: [],
    delivery: { bilingual_format: inputs.every((item) => item.name.toLocaleLowerCase().endsWith(".sdlxliff")) ? "SDLXLIFF" : inputs.every((item) => item.format !== "xliff") ? "Phrase bilingual DOCX" : "XLIFF", qa_report_name: "Xbench_QA_Report.xls", draft_suffix: "_DRAFT_NOT_FOR_DELIVERY.zip", completed_suffix: "_Delivery.zip" },
  };
}

type PortablePhraseRow = {
  rowIndex: number;
  row: Element;
  cells: Element[];
  sourceIndex: number;
  targetIndex: number;
  unitId: string;
  segmentId: string;
  source: string;
  target: string;
  status: string;
  locked: boolean;
  repeated: boolean;
};

type PortablePhraseStructure = {
  format: Exclude<MobileBilingualFormat, "xliff">;
  archive: Record<string, Uint8Array>;
  xml: string;
  documentNode: Document;
  rows: PortablePhraseRow[];
  sourceLocale: string;
  targetLocale: string;
};

async function parsePortablePhraseDocx(input: PortableBilingualInput, artifactId: string) {
  const structure = portablePhraseStructure(input.bytes, input.format as Exclude<MobileBilingualFormat, "xliff">);
  const segments: MobileSegment[] = [];
  for (let index = 0; index < structure.rows.length; index += 1) {
    const row = structure.rows[index];
    const ordinal = index + 1;
    segments.push({
      id: `${artifactId}-segment-${ordinal}`,
      artifact_id: artifactId,
      filename: input.name,
      unit_id: row.unitId,
      mid: row.segmentId,
      ordinal,
      source_text: row.source,
      target: row.target,
      source_hash: await sha256Hex(encoder.encode(row.source)),
      locked: row.locked,
      tag_signature: row.source.match(ANY_TOKEN_RE) ?? [],
      review_status: phraseReviewStatus(row.status, row.locked),
      match_origin: row.repeated ? "Phrase repeated segment" : structure.format === "phrase_review_docx" ? "Phrase external review" : "Phrase joined bilingual",
      match_percent: row.repeated ? 100 : phraseMatchPercent(row.status),
    });
  }
  return { segments, sourceLocale: structure.sourceLocale, targetLocale: structure.targetLocale };
}

function portablePhraseStructure(bytes: Uint8Array, requestedFormat: Exclude<MobileBilingualFormat, "xliff">): PortablePhraseStructure {
  const archive = unzipSync(bytes);
  const documentXml = archive["word/document.xml"];
  if (!documentXml) throw new Error("The Phrase bilingual DOCX has no Word document part.");
  const xml = decoder.decode(documentXml);
  const documentNode = new DOMParser().parseFromString(xml, "application/xml");
  if (documentNode.querySelector("parsererror")) throw new Error("The Phrase bilingual DOCX contains invalid WordprocessingML.");
  const allRows = elements(documentNode, "tr");
  const tables = elements(documentNode, "tbl");
  if (requestedFormat === "phrase_review_docx") {
    for (const table of tables) {
      const rows = directWordRows(table);
      const headerIndex = rows.findIndex((row) => {
        const headers = directWordCells(row).map((cell) => normalizedHeader(wordCellText(cell)));
        return headers.includes("segmentid") && headers.includes("segmentstatus") && headers.includes("sourcesegment") && headers.includes("targetsegment");
      });
      if (headerIndex < 0) continue;
      const headers = directWordCells(rows[headerIndex]).map((cell) => normalizedHeader(wordCellText(cell)));
      const idIndex = headers.indexOf("segmentid");
      const statusIndex = headers.indexOf("segmentstatus");
      const sourceIndex = headers.indexOf("sourcesegment");
      const targetIndex = headers.indexOf("targetsegment");
      const parsed = rows.slice(headerIndex + 1).map((row, index): PortablePhraseRow | null => {
        const cells = directWordCells(row);
        if (Math.max(idIndex, statusIndex, sourceIndex, targetIndex) >= cells.length) return null;
        const idCell = cells[idIndex];
        const segmentId = wordRunStyleText(idCell, "SegmentID") || String(index + 1);
        const unitId = wordRunStyleText(idCell, "TransUnitID") || wordCellText(idCell) || `row-${index + 1}`;
        const status = wordCellText(cells[statusIndex]);
        return {
          rowIndex: allRows.indexOf(row), row, cells, sourceIndex, targetIndex, unitId, segmentId,
          source: wordCellTokenizedText(cells[sourceIndex]), target: wordCellTokenizedText(cells[targetIndex]), status,
          locked: /\blocked\b|read[ -]?only|non[ -]?editable/iu.test(status) || wordRowHasDarkLock(row), repeated: false,
        };
      }).filter((row): row is PortablePhraseRow => Boolean(row && (row.source || row.target)));
      return { format: requestedFormat, archive, xml, documentNode, rows: parsed, sourceLocale: "en-US", targetLocale: "fr-FR" };
    }
  } else {
    let headerTableIndex = -1;
    let headerRow: Element | null = null;
    for (let tableIndex = 0; tableIndex < tables.length; tableIndex += 1) {
      const candidate = directWordRows(tables[tableIndex]).find((row) => {
        const headers = directWordCells(row).map((cell) => normalizedHeader(wordCellText(cell)));
        return headers.includes("id") && headers.some((item) => item.startsWith("source")) && headers.some((item) => item.startsWith("target"));
      });
      if (candidate) { headerTableIndex = tableIndex; headerRow = candidate; break; }
    }
    if (headerRow && headerTableIndex >= 0) {
      const headerCells = directWordCells(headerRow).map((cell) => wordCellText(cell));
      const headers = headerCells.map(normalizedHeader);
      const idIndex = headers.indexOf("id");
      const sourceIndex = headers.findIndex((item) => item.startsWith("source"));
      const targetIndex = headers.findIndex((item) => item.startsWith("target"));
      const numberIndex = headers.indexOf("") >= 0 ? 2 : headers.indexOf("segment");
      const dataRows: Element[] = [];
      for (let tableIndex = headerTableIndex; tableIndex < tables.length; tableIndex += 1) {
        for (const row of directWordRows(tables[tableIndex])) {
          if (row === headerRow) continue;
          const cells = directWordCells(row);
          if (Math.max(idIndex, sourceIndex, targetIndex) >= cells.length) continue;
          if (!wordCellText(cells[idIndex]).trim() || (!wordCellText(cells[sourceIndex]).trim() && !wordCellText(cells[targetIndex]).trim())) continue;
          dataRows.push(row);
        }
      }
      const parsed = dataRows.map((row, index) => {
        const cells = directWordCells(row);
        const unitId = wordCellText(cells[idIndex]).trim() || `row-${index + 1}`;
        const segmentId = numberIndex >= 0 && cells[numberIndex] ? wordCellText(cells[numberIndex]).trim() || String(index + 1) : String(index + 1);
        const repeated = wordCellUsesGreyText(cells[sourceIndex]);
        return {
          rowIndex: allRows.indexOf(row), row, cells, sourceIndex, targetIndex, unitId, segmentId,
          source: wordCellTokenizedText(cells[sourceIndex]), target: wordCellTokenizedText(cells[targetIndex]), status: "",
          locked: wordRowHasDarkLock(row), repeated,
        } satisfies PortablePhraseRow;
      });
      const localeCells = tables.slice(0, headerTableIndex).flatMap((table) => directWordRows(table).flatMap(directWordCells).map(wordCellText));
      const sourceLocale = phraseLocale(headerCells[sourceIndex] || localeCells[0], "en-US");
      const targetLocale = phraseLocale(headerCells[targetIndex] || localeCells[1], "fr-FR");
      return { format: requestedFormat, archive, xml, documentNode, rows: parsed, sourceLocale, targetLocale };
    }
  }
  throw new Error("The selected DOCX does not contain a supported Phrase bilingual table.");
}

function directWordRows(table: Element) {
  return Array.from(table.children).filter((item) => item.localName === "tr");
}

function directWordCells(row: Element) {
  return Array.from(row.children).filter((item) => item.localName === "tc");
}

function wordCellText(cell: Element) {
  let output = "";
  const walker = cell.ownerDocument.createTreeWalker(cell, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.currentNode;
  while (node) {
    if (node.nodeType === Node.TEXT_NODE && node.parentElement?.localName === "t") output += node.nodeValue ?? "";
    else if (node.nodeType === Node.ELEMENT_NODE && (node as Element).localName === "tab") output += "\t";
    else if (node.nodeType === Node.ELEMENT_NODE && ["br", "cr"].includes((node as Element).localName)) output += "\n";
    node = walker.nextNode();
  }
  return output;
}

function wordRunStyleText(cell: Element, wantedStyle: string) {
  return elements(cell, "r").filter((run) => wordRunStyle(run) === wantedStyle).map((run) => elements(run, "t").map((item) => item.textContent ?? "").join("")).join("");
}

function wordRunStyle(run: Element) {
  return directChild(directChild(run, "rPr") ?? run, "rStyle")?.getAttributeNS("http://schemas.openxmlformats.org/wordprocessingml/2006/main", "val")
    || directChild(directChild(run, "rPr") ?? run, "rStyle")?.getAttribute("w:val")
    || directChild(directChild(run, "rPr") ?? run, "rStyle")?.getAttribute("val")
    || "";
}

function wordCellTokenizedText(cell: Element) {
  let output = "";
  let pendingTag = "";
  const flushTag = () => { if (pendingTag) { output += phraseTagLiteralToTokens(pendingTag); pendingTag = ""; } };
  const walker = cell.ownerDocument.createTreeWalker(cell, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.currentNode;
  while (node) {
    if (node.nodeType === Node.TEXT_NODE && node.parentElement?.localName === "t") {
      let run: Element | null = node.parentElement;
      while (run && run.localName !== "r") run = run.parentElement;
      if (run && wordRunStyle(run).toLocaleLowerCase() === "tag") pendingTag += node.nodeValue ?? "";
      else { flushTag(); output += node.nodeValue ?? ""; }
    } else if (node.nodeType === Node.ELEMENT_NODE && (node as Element).localName === "tab") { flushTag(); output += "\t"; }
    else if (node.nodeType === Node.ELEMENT_NODE && ["br", "cr"].includes((node as Element).localName)) { flushTag(); output += "\n"; }
    node = walker.nextNode();
  }
  flushTag();
  return output;
}

function phraseTagLiteralToTokens(value: string) {
  return value.replace(/<(\/)?(\d+)(\/?)>/gu, (_, closing: string | undefined, id: string, standalone: string | undefined) => closing ? `⟦/T${id}⟧` : standalone ? `⟦R${id}⟧` : `⟦T${id}⟧`);
}

function wordRowHasDarkLock(row: Element) {
  const fills = elements(row, "shd").map((item) => (item.getAttribute("w:fill") || item.getAttribute("fill") || item.getAttributeNS("http://schemas.openxmlformats.org/wordprocessingml/2006/main", "fill") || "").toLocaleUpperCase());
  return fills.some((fill) => ["BBBFBF", "A6A6A6", "808080", "7F7F7F", "666666"].includes(fill));
}

function wordCellUsesGreyText(cell: Element) {
  return elements(cell, "color").some((item) => ["A6A6A6", "B7B7B7", "808080"].includes((item.getAttribute("w:val") || item.getAttribute("val") || item.getAttributeNS("http://schemas.openxmlformats.org/wordprocessingml/2006/main", "val") || "").toLocaleUpperCase()));
}

function phraseReviewStatus(status: string, locked: boolean) {
  if (locked) return "protected";
  if (/rejected|issue|error/iu.test(status)) return "flagged";
  if (/confirmed|translated|approved|completed/iu.test(status)) return "validated";
  return "pending";
}

function phraseMatchPercent(status: string) {
  const value = Number(status.match(/(\d{1,3})\s*%/u)?.[1] ?? "");
  return Number.isFinite(value) ? value : null;
}

function phraseLocale(value: string, fallback: string) {
  const match = value.match(/\b([a-z]{2})[-_ ]([a-z]{2})\b/iu);
  return match ? `${match[1].toLocaleLowerCase()}-${match[2].toLocaleUpperCase()}` : fallback;
}

function sourceCellRows(documentNode: Document) {
  const result = new Map<string, number>();
  for (const definition of elements(documentNode, "cxt-def")) {
    const id = definition.getAttribute("id");
    if (!id) continue;
    const cellReference = elements(definition, "value")
      .find((item) => item.getAttribute("key") === "CellReference")
      ?.textContent?.trim();
    const match = cellReference?.match(/\$?[A-Z]+\$?(\d+)$/i);
    if (match) result.set(id, Number(match[1]));
  }
  return result;
}

function sourceRowForUnit(unit: Element, contextRows: Map<string, number>) {
  let group: Element | null = unit.parentElement;
  while (group && group.localName !== "group") group = group.parentElement;
  if (!group) return null;
  for (const context of elements(group, "cxt")) {
    const row = contextRows.get(context.getAttribute("id") ?? "");
    if (row) return row;
  }
  return null;
}

function portableMimeType(name: string) {
  const lower = name.toLocaleLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".xlsm")) return "application/vnd.ms-excel.sheet.macroEnabled.12";
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  if (lower.endsWith(".ods")) return "application/vnd.oasis.opendocument.spreadsheet";
  if (lower.endsWith(".csv")) return "text/csv";
  if (lower.endsWith(".tsv")) return "text/tab-separated-values";
  if (lower.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".doc")) return "application/msword";
  if (lower.endsWith(".odt")) return "application/vnd.oasis.opendocument.text";
  if (lower.endsWith(".rtf")) return "application/rtf";
  if (lower.endsWith(".pptx")) return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  if (lower.endsWith(".ppt")) return "application/vnd.ms-powerpoint";
  if (lower.endsWith(".odp")) return "application/vnd.oasis.opendocument.presentation";
  if (lower.endsWith(".html") || lower.endsWith(".htm")) return "text/html";
  if (lower.endsWith(".md")) return "text/markdown";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".avif")) return "image/avif";
  if (lower.endsWith(".heic")) return "image/heic";
  if (lower.endsWith(".heif")) return "image/heif";
  return "image/jpeg";
}

function portableZipPath(name: string) {
  const parts = name.replaceAll("\\", "/").split("/").filter((part) => part && part !== "." && part !== "..");
  return parts.join("/") || "bilingual.sdlxliff";
}

async function attachPortableResources(bundle: MobileJobBundle, inputs: PortableResourceInput[], signal?: AbortSignal): Promise<MobileJobBundle> {
  if (!inputs.length) return bundle;
  const tmEntries = [...(bundle.tm_entries ?? [])];
  const glossaryEntries = [...(bundle.glossary_entries ?? [])];
  const memories = [...(bundle.resources?.translation_memories ?? [])];
  const glossaries = [...(bundle.resources?.glossaries ?? [])];
  const knownResources = new Set([...memories, ...glossaries].map((item) => `${item.name.toLocaleLowerCase()}\u0000${item.format}`));
  for (const input of inputs) {
    await portableImportYield(signal);
    const format = input.name.split(".").at(-1)?.toLocaleLowerCase() ?? "unknown";
    const key = `${input.name.toLocaleLowerCase()}\u0000${format}`;
    if (knownResources.has(key)) continue;
    knownResources.add(key);
    const id = `phone-resource-${(await sha256Hex(input.bytes)).slice(0, 16)}`;
    if (input.kind === "tm") {
      const parsed = format === "tmx" ? parsePortableTmx(input.bytes, input.name) : [];
      tmEntries.push(...parsed);
      memories.push({
        id,
        name: input.name,
        format,
        selected: true,
        entry_count: format === "tmx" ? parsed.length : null,
        note: format === "sdltm" ? "Native SDLTM is indexed by Codex before a job is served." : null,
      });
    } else {
      const parsed = format === "tbx" ? parsePortableTbx(input.bytes, input.name) : [];
      glossaryEntries.push(...parsed);
      glossaries.push({
        id,
        name: input.name,
        format,
        selected: true,
        entry_count: format === "tbx" ? parsed.length : null,
        note: format === "sdltb" ? "Native SDLTB is resolved by Codex before a job is served." : null,
      });
    }
  }
  return {
    ...bundle,
    tm_entries: deduplicateTmEntries(tmEntries),
    glossary_entries: deduplicateGlossaryEntries(glossaryEntries),
    resources: { translation_memories: memories, glossaries },
  };
}

function parsePortableTmx(bytes: Uint8Array, origin: string): MobileTmEntry[] {
  const documentNode = new DOMParser().parseFromString(decoder.decode(bytes), "application/xml");
  if (documentNode.querySelector("parsererror")) return [];
  const results: MobileTmEntry[] = [];
  for (const [index, unit] of elements(documentNode, "tu").entries()) {
    const variants = elements(unit, "tuv").map((item) => ({
      language: (item.getAttribute("xml:lang") || item.getAttributeNS("http://www.w3.org/XML/1998/namespace", "lang") || "").toLocaleLowerCase(),
      text: tokenizedContent(elements(item, "seg")[0] ?? item).trim(),
    }));
    const source = variants.find((item) => /^en(?:-|$)/.test(item.language)) ?? variants[0];
    const target = variants.find((item) => /^fr(?:-|$)/.test(item.language)) ?? variants.find((item) => item !== source);
    if (!source?.text || !target?.text) continue;
    results.push({ id: `tmx-${normalizedHeader(origin)}-${index + 1}`, source: source.text, target: target.text, origin });
  }
  return results;
}

function parsePortableTbx(bytes: Uint8Array, origin: string): NonNullable<MobileJobBundle["glossary_entries"]> {
  const documentNode = new DOMParser().parseFromString(decoder.decode(bytes), "application/xml");
  if (documentNode.querySelector("parsererror")) return [];
  const results: NonNullable<MobileJobBundle["glossary_entries"]> = [];
  for (const [index, entry] of elements(documentNode, "termEntry").entries()) {
    const variants = elements(entry, "langSet").map((item) => ({
      language: (item.getAttribute("xml:lang") || item.getAttributeNS("http://www.w3.org/XML/1998/namespace", "lang") || "").toLocaleLowerCase(),
      term: elements(item, "term")[0]?.textContent?.trim() ?? "",
    }));
    const source = variants.find((item) => /^en(?:-|$)/.test(item.language));
    const target = variants.find((item) => /^fr(?:-|$)/.test(item.language));
    if (!source?.term || !target?.term) continue;
    results.push({ id: `tbx-${normalizedHeader(origin)}-${index + 1}`, source_term: source.term, target_term: target.term, authority: origin, status: "approved" });
  }
  return results;
}

async function attachPortableGlossaryEvidence(bundle: MobileJobBundle, confirmedWorkbookIds = new Set<string>(), signal?: AbortSignal): Promise<MobileJobBundle> {
  // Every workbook is a candidate. The filename is useful evidence, but real
  // client glossaries are often called only "Language Assets.xlsx" or use a
  // project code. Classification therefore happens from the workbook's own
  // sheet/header/row structure below.
  const glossaryWorkbooks = (bundle.reference_files ?? []).filter((item) => {
    const lower = item.name.toLocaleLowerCase();
    return WORKBOOK_EXTENSIONS.some((extension) => lower.endsWith(extension));
  });
  const glossaryEntries = [...(bundle.glossary_entries ?? [])];
  const glossaryResources = [...(bundle.resources?.glossaries ?? [])];
  for (const workbook of glossaryWorkbooks) {
    await portableImportYield(signal);
    const rows = await readPortableWorkbookRows(workbook, signal);
    const detection = detectPortableGlossaryWorkbook(workbook, rows, confirmedWorkbookIds.has(workbook.id));
    if (!detection) continue;
    const workbookEntries: NonNullable<MobileJobBundle["glossary_entries"]> = [];
    for (const row of detection.rows) {
      const sourceCell = row.cells.find((cell) => isEnglishReferenceHeader(cell.column));
      if (!sourceCell?.value.trim()) continue;
      const dnt = /dnt/i.test(`${workbook.name} ${row.sheet}`);
      const targetCell = row.cells.find((cell) => isFrenchReferenceHeader(cell.column));
      const target = dnt ? sourceCell.value : targetCell?.value;
      if (!target?.trim()) continue;
      workbookEntries.push({
        id: `workbook-term-${normalizedHeader(workbook.name)}-${normalizedHeader(row.sheet)}-${row.rowNumber}`,
        source_term: sourceCell.value.trim(),
        target_term: target.trim(),
        authority: `${row.sheet} · ${basename(workbook.name)}`,
        status: "approved",
      });
    }
    glossaryEntries.push(...workbookEntries);
    const entryCount = deduplicateGlossaryEntries(workbookEntries).length;
    const resourceKey = workbook.name.toLocaleLowerCase();
    const existingIndex = glossaryResources.findIndex((item) => item.name.toLocaleLowerCase() === resourceKey);
    if (existingIndex < 0) {
      glossaryResources.push({ id: workbook.id, name: workbook.name, format: workbook.name.split(".").at(-1)?.toLocaleLowerCase() ?? "workbook", selected: true, entry_count: entryCount, note: detection.note });
    } else if (glossaryResources[existingIndex].entry_count !== entryCount || glossaryResources[existingIndex].note !== detection.note) {
      glossaryResources[existingIndex] = { ...glossaryResources[existingIndex], entry_count: entryCount, note: detection.note };
    }
  }
  const deduplicated = deduplicateGlossaryEntries(glossaryEntries);
  if (!deduplicated.length && !glossaryResources.length) return bundle;
  const companions: MobileJobBundle["companions"] = { ...bundle.companions };
  for (const [segmentIndex, segment] of bundle.segments.entries()) {
    if (segmentIndex % 24 === 0) await portableImportYield(signal);
    const current = companions[segment.id] ?? { tm_matches: [], terms: [], screenshots: [], context: {} };
    const seen = new Set(current.terms.map((item) => `${normalizedReferenceText(item.source_term)}\u0000${normalizedReferenceText(item.target_term ?? "")}`));
    const additions = deduplicated.filter((term) => termApplies(segment.source_text, term.source_term)).filter((term) => {
      const key = `${normalizedReferenceText(term.source_term)}\u0000${normalizedReferenceText(term.target_term)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 40);
    companions[segment.id] = { ...current, terms: [...current.terms, ...additions] };
  }
  return {
    ...bundle,
    glossary_entries: deduplicated,
    companions,
    resources: { translation_memories: bundle.resources?.translation_memories ?? [], glossaries: glossaryResources },
  };
}

function isFrenchReferenceHeader(header: string) {
  const key = normalizedHeader(header);
  return ["vfr", "target", "targettext", "targetterm", "french", "frenchterm", "frfr", "valuesfr"].includes(key)
    || key.startsWith("french") || key.startsWith("targetterm") || key.startsWith("preferredfrench");
}

function detectPortableGlossaryWorkbook(workbook: MobileReferenceFile, rows: PortableWorkbookRow[], confirmedTerminologyIntent = false) {
  const filename = (workbook.name.toLocaleLowerCase().split("/").at(-1) ?? workbook.name.toLocaleLowerCase());
  const explicitFilename = /glossar|termbase|(?:key|general)[ _-]?terms?|(?:^|[ _-])dnt(?:[ _.-]|$)/.test(filename);
  const dntFilename = /(?:^|[ _-])dnt(?:[ _.-]|$)/.test(filename);
  const bySheet = new Map<string, PortableWorkbookRow[]>();
  for (const row of rows) bySheet.set(row.sheet, [...(bySheet.get(row.sheet) ?? []), row]);
  const detectedRows: PortableWorkbookRow[] = [];
  const detectedSheets: string[] = [];
  let detectedByStructure = false;
  for (const [sheet, sheetRows] of bySheet) {
    const headers = sheetRows[0]?.headers ?? [];
    if (!headers.some(isEnglishReferenceHeader) || (!headers.some(isFrenchReferenceHeader) && !dntFilename)) continue;
    const keys = headers.map(normalizedHeader);
    const hasReferenceSignals = keys.some((key) => [
      "screenshot", "stringid", "requireid", "numberid", "uid", "revisereason", "stringdescription", "segmentid", "sourcehash",
    ].includes(key));
    const terminologyHeaders = keys.filter((key) => /term|preferred|approved|forbidden|avoid|inconsistent|partofspeech|domain|definition|status|authority|dnt/.test(key)).length;
    const sheetLooksTerminological = /terms?|terminology|glossar|termbase|lexicon|vocabulary/i.test(sheet);
    const pairedRows = sheetRows.filter((row) => {
      const source = row.cells.find((cell) => isEnglishReferenceHeader(cell.column))?.value.trim() ?? "";
      const target = row.cells.find((cell) => isFrenchReferenceHeader(cell.column))?.value.trim() ?? "";
      return Boolean(source && (target || dntFilename));
    });
    if (!pairedRows.length) continue;
    const termLikeRows = pairedRows.filter((row) => {
      const source = row.cells.find((cell) => isEnglishReferenceHeader(cell.column))?.value.trim() ?? "";
      const target = row.cells.find((cell) => isFrenchReferenceHeader(cell.column))?.value.trim() ?? "";
      return source.split(/\s+/u).length <= 10 && target.split(/\s+/u).length <= 14 && !/[\r\n]/u.test(`${source}${target}`);
    }).length;
    const explicitTerminologyStructure = terminologyHeaders >= 2 && (sheetLooksTerminological || keys.some((key) => /preferred|approved|forbidden|avoid|dnt/.test(key)));
    const termShapeRatio = pairedRows.length ? termLikeRows / pairedRows.length : 0;
    const filenameSupported = explicitFilename && (sheetLooksTerminological || terminologyHeaders >= 1 || (headers.length <= 8 && termShapeRatio >= .7));
    const structurallyConfident = explicitTerminologyStructure || (pairedRows.length >= 1 && sheetLooksTerminological);
    const confirmed = confirmedTerminologyIntent && pairedRows.length > 0;
    if (hasReferenceSignals || (!confirmed && !filenameSupported && !structurallyConfident)) continue;
    detectedRows.push(...pairedRows);
    detectedSheets.push(sheet);
    if (structurallyConfident && !explicitFilename) detectedByStructure = true;
  }
  if (!detectedRows.length) return null;
  const sheetLabel = detectedSheets.length === 1 ? `${detectedSheets[0]} sheet` : `${detectedSheets.slice(0, -1).join(", ")} and ${detectedSheets.at(-1)} sheets`;
  return {
    rows: detectedRows,
    note: confirmedTerminologyIntent
      ? `Confirmed as terminology from Add TM/TB; detected bilingual term columns in the ${sheetLabel}.`
      : detectedByStructure
        ? `Automatically detected as a glossary from the ${sheetLabel} structure.`
        : `Detected from the filename and bilingual term structure in the ${sheetLabel}.`,
  };
}

function termApplies(sourceText: string, sourceTerm: string) {
  const source = normalizedReferenceText(sourceText);
  const term = normalizedReferenceText(sourceTerm);
  if (!term || term.length < 2) return false;
  return source === term || source.includes(term);
}

function deduplicateTmEntries(entries: MobileTmEntry[]) {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    const key = `${normalizedReferenceText(entry.source)}\u0000${normalizedReferenceText(entry.target)}`;
    if (!entry.source.trim() || !entry.target.trim() || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function searchMobileTm(bundle: MobileJobBundle, query: string, limit = 40) {
  const wanted = normalizedReferenceText(query);
  if (!wanted) return [] as Array<MobileTmEntry & { score: number }>;
  const pool: MobileTmEntry[] = [...(bundle.tm_entries ?? [])];
  for (const companion of Object.values(bundle.companions)) {
    for (const match of companion.tm_matches) pool.push({ id: match.id, source: match.source, target: match.target, origin: match.origin });
  }
  for (const segment of bundle.segments) {
    if (segment.target.trim()) pool.push({ id: `job-segment-${segment.id}`, source: segment.source_text, target: segment.target, origin: segment.filename });
  }
  return deduplicateTmEntries(pool)
    .map((entry) => ({ ...entry, score: portableTmScore(wanted, normalizedReferenceText(entry.source)) }))
    .filter((entry) => entry.score >= 42)
    .sort((left, right) => right.score - left.score || left.source.length - right.source.length || left.origin.localeCompare(right.origin))
    .slice(0, Math.max(1, limit));
}

function portableTmScore(query: string, source: string) {
  if (query === source) return 100;
  if (source.includes(query)) return Math.max(82, 97 - Math.min(15, Math.round((source.length - query.length) / 8)));
  if (query.includes(source) && source.length >= 4) return Math.max(72, 92 - Math.min(18, Math.round((query.length - source.length) / 8)));
  const left = portableNgrams(query);
  const right = portableNgrams(source);
  if (!left.size || !right.size) return 0;
  let overlap = 0;
  for (const value of left) if (right.has(value)) overlap += 1;
  return Math.round((2 * overlap / (left.size + right.size)) * 100);
}

function portableNgrams(value: string) {
  const compact = ` ${value.replace(/\s+/g, " ")} `;
  const result = new Set<string>();
  const width = compact.length < 8 ? 2 : 3;
  for (let index = 0; index <= compact.length - width; index += 1) result.add(compact.slice(index, index + width));
  return result;
}

function deduplicateGlossaryEntries(entries: NonNullable<MobileJobBundle["glossary_entries"]>) {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    const key = `${normalizedReferenceText(entry.source_term)}\u0000${normalizedReferenceText(entry.target_term)}`;
    if (!entry.source_term.trim() || !entry.target_term.trim() || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function reviewStatusFor(confirmation: string) {
  if (confirmation === "ApprovedSignOff") return "signed_off";
  if (confirmation === "ApprovedTranslation") return "approved";
  if (confirmation.startsWith("Rejected")) return "flagged";
  if (confirmation === "Translated") return "validated";
  return "pending";
}

function commonJobName(names: string[]) {
  if (names.length === 1) return names[0].replace(/\.(?:sdlxliff|xliff|xlf)$/i, "");
  let prefix = names[0];
  for (const name of names.slice(1)) while (prefix && !name.startsWith(prefix)) prefix = prefix.slice(0, -1);
  return prefix.replace(/[_ .-]+$/, "") || `${names.length}-file translation job`;
}

async function sha256Hex(value: Uint8Array) {
  const digest = await crypto.subtle.digest("SHA-256", new Uint8Array(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function bytesToBase64(value: Uint8Array) {
  let result = "";
  const size = 0x8000;
  for (let index = 0; index < value.length; index += size) result += String.fromCharCode(...value.subarray(index, index + size));
  return btoa(result);
}

export async function decryptMobileBundle(envelope: MobileEnvelope, passphrase: string): Promise<MobileJobBundle> {
  const value = await decryptEnvelope(envelope, passphrase);
  const bundle = JSON.parse(decoder.decode(value)) as MobileJobBundle;
  if (bundle.version !== 1 || bundle.kind !== "translation-command-center-mobile-job") throw new Error("This mobile job format is not supported.");
  return bundle;
}

export async function encryptMobilePatch(patch: MobilePatch, passphrase: string): Promise<MobileEnvelope> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const iterations = 310_000;
  const key = await deriveKey(passphrase, salt, iterations, ["encrypt"]);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: encoder.encode("tcc-mobile-v1") },
    key,
    encoder.encode(JSON.stringify(patch)),
  );
  return {
    version: 1,
    algorithm: "AES-256-GCM",
    kdf: "PBKDF2-SHA256",
    iterations,
    salt: base64Url(new Uint8Array(salt)),
    iv: base64Url(new Uint8Array(iv)),
    ciphertext: base64Url(new Uint8Array(ciphertext)),
  };
}

export async function decryptMobilePatch(envelope: MobileEnvelope, passphrase: string): Promise<MobilePatch> {
  const value = await decryptEnvelope(envelope, passphrase);
  const patch = JSON.parse(decoder.decode(value)) as MobilePatch;
  if (patch.version !== 1 || !Array.isArray(patch.changes)) throw new Error("This encrypted phone review is not supported.");
  return patch;
}

async function decryptEnvelope(envelope: MobileEnvelope, passphrase: string): Promise<ArrayBuffer> {
  if (envelope.algorithm !== "AES-256-GCM" || envelope.kdf !== "PBKDF2-SHA256") throw new Error("Unsupported mobile encryption.");
  const salt = fromBase64Url(envelope.salt);
  const iv = fromBase64Url(envelope.iv);
  const key = await deriveKey(passphrase, salt, envelope.iterations, ["decrypt"]);
  try {
    return await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, additionalData: encoder.encode("tcc-mobile-v1") },
      key,
      fromBase64Url(envelope.ciphertext),
    );
  } catch {
    throw new Error("This mobile link is invalid, expired, or has the wrong encryption key.");
  }
}

async function deriveKey(passphrase: string, salt: Uint8Array, iterations: number, usages: KeyUsage[]) {
  const material = await crypto.subtle.importKey("raw", encoder.encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt: new Uint8Array(salt), iterations },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    usages,
  );
}

export function currentMobileQa(segments: MobileSegment[]): MobileQaIssue[] {
  const issues: MobileQaIssue[] = [];
  const bySource = new Map<string, Map<string, MobileSegment[]>>();
  for (const segment of segments) {
    if (segment.locked) continue;
    const source = segment.source_text;
    const target = segment.target;
    const add = (code: string, category: string, severity: "critical" | "major" | "minor", message: string, reportTitle?: string) => issues.push({
      segment_id: segment.id, code, category, severity, message, report_title: reportTitle,
      source_value: source, target_value: target, filename: segment.filename, segment_number: segment.ordinal,
      match_percent: segment.match_percent, match_origin: segment.match_origin, review_status: segment.review_status,
    });
    if (!target.trim()) add("empty_target", "content", "critical", "The target is empty.", "Untranslated Segment");
    const sourceTags = source.match(ANY_TOKEN_RE) ?? [];
    const targetTags = target.match(ANY_TOKEN_RE) ?? [];
    if (sourceTags.slice().sort().join("\0") !== targetTags.slice().sort().join("\0")) add("inline_tag_mismatch", "tags", "critical", "Inline tags differ from the source.", "Tag Mismatch");
    const placeholders = (value: string) => value.match(/%(?:\d+\$)?[sdif]|\{\d+\}|\$\{[^}]+\}|<PI\d+\/>|\\[nrt]|https?:\/\/\S+/gi) ?? [];
    if (placeholders(source).sort().join("\0") !== placeholders(target).sort().join("\0")) add("placeholder_mismatch", "variables", "critical", "Variables, links, or placeholders differ.", "Alphanumeric Mismatch");
    const numbers = (value: string) => value.match(/(?<!\w)[+-]?\d+(?:[.,]\d+)*(?:\s?(?:%|°[CF]|mL|L|W|V|Pa|mAh|GHz|mm|cm|m))?/gi) ?? [];
    if (numbers(source).sort().join("\0") !== numbers(target).sort().join("\0")) add("number_or_unit_mismatch", "numbers", "major", "Numbers or units differ.", "Numeric Mismatch");
    if (/\b([A-Za-zÀ-ÖØ-öø-ÿ]{2,})\s+\1\b/i.test(target)) add("repeated_word", "language", "major", "A word is repeated.", "Repeated Word");
    if (/\d (?=(?:%|°[CF]|mL|L|W|V|Pa|mAh|GHz|mm|cm|m)\b)/i.test(target)) add("breakable_unit_space", "typography", "minor", "A number and unit use a breakable space.", "Checklist: French non-breaking space");
    if (source.trim().toLocaleLowerCase() === target.trim().toLocaleLowerCase() && /[A-Za-z]{4}/.test(source)) add("source_equals_target", "content", "major", "Source and target are identical.", "Target same as Source");
    const key = source.trim().toLocaleLowerCase();
    const targetKey = target.trim().toLocaleLowerCase();
    if (key && targetKey) {
      const targets = bySource.get(key) ?? new Map<string, MobileSegment[]>();
      targets.set(targetKey, [...(targets.get(targetKey) ?? []), segment]);
      bySource.set(key, targets);
    }
  }
  for (const targets of bySource.values()) {
    if (targets.size <= 1) continue;
    for (const rows of targets.values()) for (const segment of rows) issues.push({
      segment_id: segment.id, category: "consistency", code: "same_source_different_target", severity: "major",
      message: "The same source has multiple French translations.", report_title: "Inconsistency in Target",
      source_value: segment.source_text, target_value: segment.target, filename: segment.filename, segment_number: segment.ordinal,
      match_percent: segment.match_percent, match_origin: segment.match_origin, review_status: segment.review_status,
    });
  }
  return issues;
}

export function checklistMobileQa(bundle: MobileJobBundle, segments: MobileSegment[]) {
  const issues: MobileQaIssue[] = [];
  const unsupported = new Set<string>();
  for (const checklist of bundle.checklists) {
    const documentNode = new DOMParser().parseFromString(checklist.xml, "application/xml");
    if (documentNode.querySelector("parsererror")) {
      unsupported.add(checklist.name);
      continue;
    }
    for (const check of Array.from(documentNode.getElementsByTagName("check"))) {
      const name = check.getAttribute("name") || "Checklist";
      const description = check.getElementsByTagName("description")[0]?.textContent?.trim() || name;
      const sourceTerm = Array.from(check.getElementsByTagName("term")).find((item) => item.getAttribute("type") === "source");
      const targetTerm = Array.from(check.getElementsByTagName("term")).find((item) => item.getAttribute("type") === "target");
      if (!sourceTerm || !targetTerm) {
        unsupported.add(`${checklist.name}: ${name}`);
        continue;
      }
      const sourceMatcher = checklistMatcher(sourceTerm);
      const targetMatcher = checklistMatcher(targetTerm);
      if (!sourceMatcher || !targetMatcher) {
        unsupported.add(`${checklist.name}: ${name}`);
        continue;
      }
      for (const segment of segments) {
        if (segment.locked || !sourceMatcher(segment.source_text) || !targetMatcher(segment.target)) continue;
        issues.push({
          segment_id: segment.id,
          category: "checklist",
          code: `checklist_${name.toLocaleLowerCase().replace(/[^a-z0-9]+/g, "_")}`,
          severity: "major",
          message: description,
          report_title: `Checklist: ${name}${description !== name ? ` (${description})` : ""}`,
          source_value: segment.source_text,
          target_value: segment.target,
          filename: segment.filename,
          segment_number: segment.ordinal,
          match_percent: segment.match_percent,
          match_origin: segment.match_origin,
          review_status: segment.review_status,
        });
      }
    }
  }
  if (unsupported.size) {
    const first = segments.find((item) => !item.locked) ?? segments[0];
    if (first) issues.push({
      segment_id: first.id,
      category: "coverage",
      code: "unsupported_checklist_expression",
      severity: "critical",
      message: `${unsupported.size} checklist expression(s) cannot be reproduced safely in the phone browser.`,
      report_title: "Checklist coverage blocker",
      source_value: first.source_text,
      target_value: first.target,
      filename: first.filename,
      segment_number: first.ordinal,
      review_status: first.review_status,
    });
  }
  return { issues, unsupported: [...unsupported] };
}

export function mobileCompletion(segments: MobileSegment[], issues: MobileQaIssue[]) {
  const unconfirmed = segments.filter((item) => !item.locked && !CONFIRMED.has(item.review_status));
  // Linguistic findings and checklist coverage warnings belong in the report,
  // but must not turn an otherwise confirmed phone branch into a draft. Only
  // defects that make the bilingual payload structurally incomplete are final
  // delivery blockers.
  const hard = issues.filter((item) => ["empty_target", "inline_tag_mismatch", "placeholder_mismatch"].includes(item.code));
  return { complete: unconfirmed.length === 0 && hard.length === 0, unconfirmed: unconfirmed.length, hardIssues: hard.length };
}

export async function renderMobileXbenchReport(
  issues: MobileQaIssue[],
  inventory: MobileJobBundle["qa_profile"],
): Promise<Uint8Array> {
  const imported = await import("xlsx-js-style");
  const XLSX = imported.default ?? imported;
  const rows: unknown[][] = [
    ["Exported QA Report", "", "", "Generated by", ""],
    ["", "", "", "Translation Command Center QA", ""],
    ["", "", "", "https://victorthecomposer.net/TCC", ""],
    [],
    ["Selected QA Checks", "", "", ""],
    ["Basic", ...(inventory.basic?.length ? [inventory.basic.join(", ")] : ["Untranslated Segments, Inconsistency in Source, Inconsistency in Target, Target same as Source"]), "", ""],
    ["Content", ...(inventory.content?.length ? [inventory.content.join(", ")] : ["Tag Mismatch, Numeric Mismatch, URL Mismatch, Alphanumeric Mismatch, Unpaired Symbol, Unpaired Quotes, Double Blank, Repeated Word, Key Term Mismatch"]), "", ""],
    ["Key Term Glossaries", inventory.glossaries?.join(", ") || "None", "", ""],
    ["Checklists", inventory.checklists?.join(", ") || "Project Checklist", "", ""],
    [],
    ["", "", "Source", "Target", "Comments"],
  ];
  let currentGroup = "";
  const groupRows = new Set<number>();
  for (const issue of issues) {
    const group = issue.report_title ?? issue.category ?? "QA Issue";
    if (group !== currentGroup) {
      groupRows.add(rows.length);
      rows.push([group, "", "", "", ""]);
      currentGroup = group;
    }
    const status = issue.review_status || "Draft";
    const origin = issue.match_origin || "interactive";
    rows.push([
      `${issue.filename || "Bilingual file"} (${issue.segment_number || "?"})`,
      issue.match_percent == null ? "0%" : `${Math.round(issue.match_percent)}%`,
      issue.source_value || "",
      issue.target_value || "",
      `${issue.message ? `${issue.message}\n` : ""}${issue.match_percent == null ? "0" : Math.round(issue.match_percent)}%-${status === "validated" ? "Translated" : "Untranslated"}\nStatus: ${status === "validated" ? "Translated" : "Draft"}\nOrigin: ${origin}`,
    ]);
  }
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  // These values round-trip to the native BIFF8 widths found in the modal
  // 50-report Xbench corpus: 5302, 1718, 12982, 12982, 10422, 10422.
  sheet["!cols"] = [5302, 1718, 12982, 12982, 10422, 10422].map((width) => ({ wch: (width - 213) / 256 }));
  sheet["!rows"] = rows.map((_, row) => {
    if (row === 3 || row === 9) return {};
    if (row === 6) return { hpt: 30 };
    if (row >= 12 && !groupRows.has(row)) return { hpt: 45 };
    return { hpt: 15 };
  });
  sheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 2, c: 2 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: 3 } },
    ...[5, 6, 7, 8].map((row) => ({ s: { r: row, c: 1 }, e: { r: row, c: 3 } })),
  ];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Feuil1");
  const value = XLSX.write(workbook, { type: "array", bookType: "biff8", cellStyles: true, bookSST: true }) as ArrayBuffer;
  const output = applyLegacyBiffStyles(XLSX, new Uint8Array(value), groupRows);
  if (output.length < 4096 || ![0xd0, 0xcf, 0x11, 0xe0].every((byte, index) => output[index] === byte)) {
    throw new Error("The browser could not create a valid legacy XLS QA report.");
  }
  return output;
}

export function writeMobileSdlxliff(file: MobileJobBundle["files"][number], segments: MobileSegment[]): Uint8Array {
  const originalBytes = fromBase64(file.content_base64);
  const hasUtf8Bom = originalBytes.length >= 3 && originalBytes[0] === 0xef && originalBytes[1] === 0xbb && originalBytes[2] === 0xbf;
  const original = decoder.decode(originalBytes);
  const documentNode = new DOMParser().parseFromString(original, "application/xml");
  const parseError = documentNode.querySelector("parsererror");
  if (parseError) throw new Error(`${file.name}: the original SDLXLIFF could not be parsed.`);
  const lexicalRoot = scanXmlElements(original);
  const replacements: XmlReplacement[] = [];
  const fileSegments = segments.filter((item) => item.artifact_id === file.artifact_id);
  for (const segment of fileSegments) {
    const unit = elements(documentNode, "trans-unit").find((item) => item.getAttribute("id") === segment.unit_id);
    const lexicalUnit = descendants(lexicalRoot, "trans-unit").find((item) => xmlAttribute(item, "id") === segment.unit_id);
    if (!unit || !lexicalUnit) throw new Error(`${file.name}: segment unit ${segment.unit_id} disappeared.`);
    const sourceContainer = directChild(unit, "seg-source") ?? directChild(unit, "source");
    const targetContainer = directChild(unit, "target");
    const lexicalSourceContainer = directXmlChild(lexicalUnit, "seg-source") ?? directXmlChild(lexicalUnit, "source");
    const lexicalTargetContainer = directXmlChild(lexicalUnit, "target");
    if (!sourceContainer || !targetContainer || !lexicalSourceContainer || !lexicalTargetContainer) {
      throw new Error(`${file.name}: segment ${segment.ordinal} has no guarded source/target pair.`);
    }
    const source = segment.mid ? elements(sourceContainer, "mrk").find((item) => item.getAttribute("mtype") === "seg" && (item.getAttribute("mid") ?? "") === segment.mid) : sourceContainer;
    const target = segment.mid ? elements(targetContainer, "mrk").find((item) => item.getAttribute("mtype") === "seg" && (item.getAttribute("mid") ?? "") === segment.mid) : targetContainer;
    const lexicalSource = segment.mid ? descendants(lexicalSourceContainer, "mrk").find((item) => xmlAttribute(item, "mtype") === "seg" && (xmlAttribute(item, "mid") ?? "") === segment.mid) : lexicalSourceContainer;
    const lexicalTarget = segment.mid ? descendants(lexicalTargetContainer, "mrk").find((item) => xmlAttribute(item, "mtype") === "seg" && (xmlAttribute(item, "mid") ?? "") === segment.mid) : lexicalTargetContainer;
    if (!source || !target || !lexicalSource || !lexicalTarget) throw new Error(`${file.name}: segment ${segment.ordinal} cannot be rewritten safely in the browser.`);
    if (tokenizedContent(source) !== segment.source_text) throw new Error(`${file.name}: source changed for segment ${segment.ordinal}.`);
    const originalTarget = tokenizedContent(target);
    if (!segment.locked && originalTarget !== segment.target) {
      const renderedTarget = renderTokenizedXml(segment.target, source, lexicalSource, target, lexicalTarget, original);
      replacements.push({
        start: lexicalTarget.selfClosing ? lexicalTarget.start : lexicalTarget.contentStart,
        end: lexicalTarget.selfClosing ? lexicalTarget.end : lexicalTarget.contentEnd,
        value: lexicalTarget.selfClosing ? expandSelfClosingXmlElement(lexicalTarget, original, renderedTarget) : renderedTarget,
        label: `target ${segment.ordinal}`,
      });
    }
    const metadata = elements(documentNode, "seg").find((item) => item.getAttribute("id") === segment.mid);
    const lexicalMetadata = segment.mid
      ? descendants(lexicalUnit, "seg").find((item) => xmlAttribute(item, "id") === segment.mid)
      : null;
    if (metadata && lexicalMetadata && !segment.locked) {
      const nextConfirmation = confirmationFor(segment.review_status);
      if ((metadata.getAttribute("conf") ?? "Draft") !== nextConfirmation) {
        replacements.push(replaceXmlAttribute(lexicalMetadata, "conf", nextConfirmation));
      }
    }
  }
  const serialized = applyXmlReplacements(original, replacements);
  const reread = new DOMParser().parseFromString(serialized, "application/xml");
  if (reread.querySelector("parsererror")) throw new Error(`${file.name}: browser serialization produced invalid XML.`);
  for (const segment of fileSegments) {
    const unit = elements(reread, "trans-unit").find((item) => item.getAttribute("id") === segment.unit_id);
    const sourceContainer = unit && (directChild(unit, "seg-source") ?? directChild(unit, "source"));
    const targetContainer = unit && directChild(unit, "target");
    const source = sourceContainer && segment.mid ? elements(sourceContainer, "mrk").find((item) => item.getAttribute("mtype") === "seg" && (item.getAttribute("mid") ?? "") === segment.mid) : sourceContainer;
    const target = targetContainer && segment.mid ? elements(targetContainer, "mrk").find((item) => item.getAttribute("mtype") === "seg" && (item.getAttribute("mid") ?? "") === segment.mid) : targetContainer;
    if (!source || tokenizedContent(source) !== segment.source_text) throw new Error(`${file.name}: source read-back failed for segment ${segment.ordinal}.`);
    if (!target || (!segment.locked && tokenizedContent(target) !== segment.target)) throw new Error(`${file.name}: target read-back failed for segment ${segment.ordinal}.`);
  }
  const encoded = encoder.encode(serialized);
  if (!hasUtf8Bom) return encoded;
  const withBom = new Uint8Array(encoded.length + 3);
  withBom.set([0xef, 0xbb, 0xbf]);
  withBom.set(encoded, 3);
  return withBom;
}

function expandSelfClosingXmlElement(node: XmlLexNode, original: string, content: string) {
  const opening = original.slice(node.start, node.end).replace(/\/\s*>$/u, ">");
  return `${opening}${content}</${node.name}>`;
}

export function writeMobileBilingual(file: MobileJobBundle["files"][number], segments: MobileSegment[]): Uint8Array {
  const original = fromBase64(file.content_base64);
  const format = file.format ?? portableBilingualFormat(file.name, original) ?? "xliff";
  return format === "xliff" ? writeMobileSdlxliff(file, segments) : writeMobilePhraseDocx(file, segments, format);
}

export function writeMobilePhraseDocx(
  file: MobileJobBundle["files"][number],
  segments: MobileSegment[],
  format = file.format as Exclude<MobileBilingualFormat, "xliff">,
): Uint8Array {
  if (format !== "phrase_review_docx" && format !== "phrase_joined_docx") throw new Error(`${file.name}: unsupported Phrase DOCX format.`);
  const originalBytes = fromBase64(file.content_base64);
  const structure = portablePhraseStructure(originalBytes, format);
  const lexicalRoot = scanXmlElements(structure.xml);
  const lexicalRows = descendants(lexicalRoot, "tr");
  const replacements: XmlReplacement[] = [];
  const fileSegments = segments.filter((item) => item.artifact_id === file.artifact_id);
  const rows = new Map(structure.rows.map((row) => [`${row.unitId}\u0000${row.segmentId}`, row]));
  for (const segment of fileSegments) {
    const row = rows.get(`${segment.unit_id}\u0000${segment.mid}`);
    if (!row) throw new Error(`${file.name}: Phrase row ${segment.unit_id}_${segment.mid} disappeared.`);
    if (row.source !== segment.source_text) throw new Error(`${file.name}: source changed for Phrase segment ${segment.ordinal}.`);
    if (segment.locked || row.locked || row.target === segment.target) continue;
    const lexicalRow = lexicalRows[row.rowIndex];
    const lexicalCells = lexicalRow ? lexicalRow.children.filter((item) => item.localName === "tc") : [];
    const lexicalTarget = lexicalCells[row.targetIndex];
    const lexicalSource = lexicalCells[row.sourceIndex];
    if (!lexicalTarget || !lexicalSource) throw new Error(`${file.name}: target cell mapping failed for Phrase segment ${segment.ordinal}.`);
    const targetProperties = directXmlChild(lexicalTarget, "tcPr");
    replacements.push({
      start: targetProperties?.end ?? lexicalTarget.contentStart,
      end: lexicalTarget.contentEnd,
      value: renderPhraseTargetCell(segment.target, row.cells[row.targetIndex], row.cells[row.sourceIndex], lexicalTarget, lexicalSource, structure.xml),
      label: `Phrase target ${segment.ordinal}`,
    });
  }
  if (!replacements.length) return originalBytes;
  const serialized = applyXmlReplacements(structure.xml, replacements);
  const updatedArchive = { ...structure.archive, "word/document.xml": encoder.encode(serialized) };
  const output = zipSync(updatedArchive, { level: 6 });
  const reread = portablePhraseStructure(output, format);
  const rereadRows = new Map(reread.rows.map((row) => [`${row.unitId}\u0000${row.segmentId}`, row]));
  for (const segment of fileSegments) {
    const row = rereadRows.get(`${segment.unit_id}\u0000${segment.mid}`);
    if (!row || row.source !== segment.source_text) throw new Error(`${file.name}: Phrase source read-back failed for segment ${segment.ordinal}.`);
    if (!segment.locked && row.target !== segment.target) throw new Error(`${file.name}: Phrase target read-back failed for segment ${segment.ordinal}.`);
  }
  return output;
}

function renderPhraseTargetCell(
  value: string,
  targetCell: Element,
  sourceCell: Element,
  lexicalTarget: XmlLexNode,
  lexicalSource: XmlLexNode,
  original: string,
) {
  const prefix = lexicalTarget.name.includes(":") ? `${lexicalTarget.name.slice(0, lexicalTarget.name.indexOf(":"))}:` : "";
  const paragraphProperties = phraseParagraphProperties(lexicalTarget, original) || phraseParagraphProperties(lexicalSource, original);
  const plainProperties = phraseRunProperties(targetCell, lexicalTarget, original, false) || phraseRunProperties(sourceCell, lexicalSource, original, false);
  const tagProperties = phraseRunProperties(targetCell, lexicalTarget, original, true) || phraseRunProperties(sourceCell, lexicalSource, original, true)
    || `<${prefix}rPr><${prefix}rStyle ${prefix}val="Tag"/></${prefix}rPr>`;
  const tokenExpression = /⟦(?:\/?T|R)\d+⟧/gu;
  let cursor = 0;
  let runs = "";
  for (const match of value.matchAll(tokenExpression)) {
    const start = match.index ?? 0;
    if (start > cursor) runs += phraseWordRun(value.slice(cursor, start), prefix, plainProperties);
    runs += phraseWordRun(phraseTokenLiteral(match[0]), prefix, tagProperties);
    cursor = start + match[0].length;
  }
  if (cursor < value.length) runs += phraseWordRun(value.slice(cursor), prefix, plainProperties);
  if (!runs) runs = `<${prefix}r>${plainProperties}<${prefix}t/></${prefix}r>`;
  return `<${prefix}p>${paragraphProperties}${runs}</${prefix}p>`;
}

function phraseParagraphProperties(cell: XmlLexNode, original: string) {
  const paragraph = descendants(cell, "p")[0];
  const properties = paragraph && directXmlChild(paragraph, "pPr");
  return properties ? original.slice(properties.start, properties.end) : "";
}

function phraseRunProperties(cell: Element, lexicalCell: XmlLexNode, original: string, tag: boolean) {
  const runs = elements(cell, "r");
  const lexicalRuns = descendants(lexicalCell, "r");
  const index = runs.findIndex((run) => (wordRunStyle(run).toLocaleLowerCase() === "tag") === tag);
  const properties = index >= 0 ? directXmlChild(lexicalRuns[index], "rPr") : null;
  return properties ? original.slice(properties.start, properties.end) : "";
}

function phraseWordRun(value: string, prefix: string, properties: string) {
  if (!value) return "";
  const parts = value.split(/(\r\n|\r|\n|\t)/u);
  let content = "";
  for (const part of parts) {
    if (!part) continue;
    if (part === "\t") content += `<${prefix}tab/>`;
    else if (part === "\r" || part === "\n" || part === "\r\n") content += `<${prefix}br/>`;
    else content += `<${prefix}t xml:space="preserve">${escapeXmlText(part)}</${prefix}t>`;
  }
  return `<${prefix}r>${properties}${content}</${prefix}r>`;
}

function phraseTokenLiteral(token: string) {
  const standalone = token.match(/^⟦R(\d+)⟧$/u);
  if (standalone) return `<${standalone[1]}/>`;
  const closing = token.match(/^⟦\/T(\d+)⟧$/u);
  if (closing) return `</${closing[1]}>`;
  const opening = token.match(/^⟦T(\d+)⟧$/u);
  if (opening) return `<${opening[1]}>`;
  return token;
}

export async function buildMobileZip(bundle: MobileJobBundle, segments: MobileSegment[], report: Uint8Array | null, draft: boolean): Promise<Blob> {
  const content: Record<string, Uint8Array> = {};
  for (const file of bundle.files) {
    if (content[file.name]) throw new Error(`Duplicate delivery filename: ${file.name}`);
    content[file.name] = writeMobileBilingual(file, segments);
  }
  if (report) content["Xbench_QA_Report.xls"] = report;
  if (draft) {
    content["DRAFT_NOT_FOR_DELIVERY.txt"] = strToU8("This package is incomplete and must not be sent to a PM.\n");
    content["delivery-manifest.json"] = strToU8(JSON.stringify({ state: "draft_not_for_delivery", created_at: new Date().toISOString(), project_id: bundle.project.id }, null, 2));
  }
  return zipMobileContent(content);
}

export async function buildMobileCodexRecoveryPack(bundle: MobileJobBundle, segments: MobileSegment[], issues: MobileQaIssue[]): Promise<Blob> {
  const patch: MobilePatch = {
    version: 1,
    project_id: bundle.project.id,
    source_package_hash: bundle.source_package_hash,
    submitted_at: new Date().toISOString(),
    complete: mobileCompletion(segments, issues).complete,
    changes: segments.map((item) => ({ segment_id: item.id, source_hash: item.source_hash, target: item.target, review_status: item.review_status })),
  };
  const content: Record<string, Uint8Array> = {
    "TCC_phone_review_patch.json": strToU8(JSON.stringify({
      authority: "phone",
      merge_policy: "phone_target_wins_for_every_listed_segment",
      project: bundle.project,
      patch,
    }, null, 2)),
    "README_FOR_CODEX.txt": strToU8(
      "This is a Translation Command Center phone-authority recovery pack.\n" +
      "Phone_Final_Bilingual contains the standalone bilingual files exported by the phone.\n" +
      "The JSON patch is also included so Codex can audit or reapply every target/status value.\n" +
      "The source package hash is provenance only; it must not block recovery or final export from this phone branch.\n" +
      "Phone target text wins for every listed segment. Re-run format-specific structural QA after applying it.\n"
    ),
    "QA/qa-issues.json": strToU8(JSON.stringify(issues, null, 2)),
  };
  for (const file of bundle.files) content[`Phone_Final_Bilingual/${portableZipPath(file.name)}`] = writeMobileBilingual(file, segments);
  return zipMobileContent(content);
}

export async function buildMobileAiReviewPack(
  bundle: MobileJobBundle,
  segments: MobileSegment[],
  issues: MobileQaIssue[],
  report: Uint8Array | null,
): Promise<Blob> {
  const content: Record<string, Uint8Array> = {};
  for (const file of bundle.files) content[`Bilingual/${portableZipPath(file.name)}`] = writeMobileBilingual(file, segments);
  if (report) content["QA/Xbench_QA_Report.xls"] = report;
  const rows = [["File", "Segment", "Status", "Source", "Target", "Reference context"]];
  for (const segment of segments) {
    const companion = bundle.companions[segment.id];
    const context = (companion?.reference_row?.cells ?? [])
      .filter((cell) => cell.value?.trim())
      .map((cell) => `${cell.column}: ${cell.value}`)
      .join(" | ");
    rows.push([segment.filename, String(segment.ordinal), segment.review_status, segment.source_text, segment.target, context]);
  }
  content["Review/bilingual-review.csv"] = strToU8(rows.map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n");
  content["Review/qa-issues.json"] = strToU8(JSON.stringify(issues, null, 2));
  content["Review/job-context.json"] = strToU8(JSON.stringify({
    project: bundle.project,
    resources: bundle.resources ?? { translation_memories: [], glossaries: [] },
    companions: Object.fromEntries(segments.map((segment) => [segment.id, {
      reference_row: bundle.companions[segment.id]?.reference_row ?? null,
      terms: bundle.companions[segment.id]?.terms ?? [],
      screenshots: (bundle.companions[segment.id]?.screenshots ?? []).map((item) => ({ ...item, image_url: item.image_url ? "included separately when portable" : "" })),
    }])),
  }, null, 2));
  for (const reference of bundle.reference_files ?? []) {
    content[`References/${portableZipPath(reference.name)}`] = fromBase64(reference.content_base64);
  }
  content["README_FOR_AI_REVIEW.txt"] = strToU8(
    "Review the French targets against the English source, QA findings, exact context rows, terminology and references.\n" +
    "Protected inline tags appear as tokens such as ⟦T0⟧ or ⟦/T0⟧ in the CSV and must remain paired and unchanged.\n" +
    "Do not rewrite source text, IDs, locked segments, tags, placeholders or the bilingual structure. Report proposed target-only corrections by file and segment number.\n"
  );
  return zipMobileContent(content);
}

function zipMobileContent(content: Record<string, Uint8Array>): Promise<Blob> {
  return new Promise((resolve, reject) => {
    // fflate uses workers where available, keeping iOS scrolling, progress
    // messaging and the system share sheet responsive during compression.
    zip(content, { level: 6 }, (error, output) => {
      if (error) reject(new Error("The phone could not compress the delivery package."));
      else resolve(new Blob([output], { type: "application/zip" }));
    });
  });
}

function csvCell(value: string) {
  return `"${value.replaceAll('"', '""')}"`;
}

type XmlLexAttribute = {
  name: string;
  localName: string;
  value: string;
  valueStart: number;
  valueEnd: number;
};

type XmlLexNode = {
  name: string;
  localName: string;
  start: number;
  startTagEnd: number;
  contentStart: number;
  contentEnd: number;
  end: number;
  selfClosing: boolean;
  attributes: XmlLexAttribute[];
  children: XmlLexNode[];
  parent: XmlLexNode | null;
};

type XmlReplacement = { start: number; end: number; value: string; label: string };

/**
 * Build a lexical element map without normalizing the XML. DOM is still used
 * for semantic safety checks, while this map lets delivery replace exact byte
 * ranges instead of serializing the whole bilingual document.
 */
function scanXmlElements(xml: string): XmlLexNode {
  const root: XmlLexNode = {
    name: "#document", localName: "#document", start: 0, startTagEnd: 0,
    contentStart: 0, contentEnd: xml.length, end: xml.length, selfClosing: false,
    attributes: [], children: [], parent: null,
  };
  const stack = [root];
  let cursor = 0;
  while (cursor < xml.length) {
    const start = xml.indexOf("<", cursor);
    if (start < 0) break;
    if (xml.startsWith("<!--", start)) {
      const end = xml.indexOf("-->", start + 4);
      if (end < 0) throw new Error("The bilingual XML contains an unterminated comment.");
      cursor = end + 3;
      continue;
    }
    if (xml.startsWith("<![CDATA[", start)) {
      const end = xml.indexOf("]]>", start + 9);
      if (end < 0) throw new Error("The bilingual XML contains an unterminated CDATA section.");
      cursor = end + 3;
      continue;
    }
    if (xml.startsWith("<?", start)) {
      const end = xml.indexOf("?>", start + 2);
      if (end < 0) throw new Error("The bilingual XML contains an unterminated processing instruction.");
      cursor = end + 2;
      continue;
    }
    const end = xmlMarkupEnd(xml, start);
    const markup = xml.slice(start, end + 1);
    if (/^<!/u.test(markup)) {
      cursor = end + 1;
      continue;
    }
    const closing = markup.match(/^<\s*\/\s*([^\s>]+)/u);
    if (closing) {
      const name = closing[1];
      const node = stack.at(-1);
      if (!node || node === root || node.name !== name) throw new Error(`The bilingual XML has an unexpected closing tag: ${name}.`);
      node.contentEnd = start;
      node.end = end + 1;
      stack.pop();
      cursor = end + 1;
      continue;
    }
    const opening = markup.match(/^<\s*([^\s/>]+)/u);
    if (!opening) throw new Error("The bilingual XML contains an unreadable tag.");
    const name = opening[1];
    const selfClosing = /\/\s*>$/u.test(markup);
    const parent = stack.at(-1) ?? root;
    const node: XmlLexNode = {
      name,
      localName: name.includes(":") ? name.slice(name.lastIndexOf(":") + 1) : name,
      start,
      startTagEnd: end + 1,
      contentStart: end + 1,
      contentEnd: selfClosing ? end + 1 : -1,
      end: selfClosing ? end + 1 : -1,
      selfClosing,
      attributes: xmlLexAttributes(markup, start),
      children: [],
      parent,
    };
    parent.children.push(node);
    if (!selfClosing) stack.push(node);
    cursor = end + 1;
  }
  if (stack.length !== 1) throw new Error(`The bilingual XML has an unterminated tag: ${stack.at(-1)?.name ?? "unknown"}.`);
  return root;
}

function xmlMarkupEnd(xml: string, start: number) {
  let quote = "";
  let subsetDepth = 0;
  for (let index = start + 1; index < xml.length; index += 1) {
    const character = xml[index];
    if (quote) {
      if (character === quote) quote = "";
      continue;
    }
    if (character === '"' || character === "'") quote = character;
    else if (character === "[") subsetDepth += 1;
    else if (character === "]" && subsetDepth) subsetDepth -= 1;
    else if (character === ">" && !subsetDepth) return index;
  }
  throw new Error("The bilingual XML contains an unterminated tag.");
}

function xmlLexAttributes(markup: string, absoluteStart: number): XmlLexAttribute[] {
  const result: XmlLexAttribute[] = [];
  const expression = /([^\s=/>]+)\s*=\s*(["'])([\s\S]*?)\2/gu;
  for (const match of markup.matchAll(expression)) {
    const whole = match[0];
    const quoteOffset = whole.indexOf(match[2]);
    const valueStart = absoluteStart + (match.index ?? 0) + quoteOffset + 1;
    const name = match[1];
    result.push({
      name,
      localName: name.includes(":") ? name.slice(name.lastIndexOf(":") + 1) : name,
      value: decodeXmlScalar(match[3]),
      valueStart,
      valueEnd: valueStart + match[3].length,
    });
  }
  return result;
}

function decodeXmlScalar(value: string) {
  return value
    .replace(/&#x([0-9a-f]+);/giu, (_, digits: string) => String.fromCodePoint(Number.parseInt(digits, 16)))
    .replace(/&#([0-9]+);/gu, (_, digits: string) => String.fromCodePoint(Number.parseInt(digits, 10)))
    .replaceAll("&quot;", '"')
    .replaceAll("&apos;", "'")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&amp;", "&");
}

function escapeXmlText(value: string) {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function escapeXmlAttribute(value: string) {
  return escapeXmlText(value).replaceAll('"', "&quot;").replaceAll("'", "&apos;");
}

function descendants(root: XmlLexNode, localName: string): XmlLexNode[] {
  const result: XmlLexNode[] = [];
  const visit = (node: XmlLexNode) => {
    for (const child of node.children) {
      if (child.localName === localName) result.push(child);
      visit(child);
    }
  };
  visit(root);
  return result;
}

function directXmlChild(root: XmlLexNode, localName: string) {
  return root.children.find((item) => item.localName === localName) ?? null;
}

function xmlAttribute(node: XmlLexNode, localName: string) {
  return node.attributes.find((item) => item.localName === localName)?.value ?? null;
}

function replaceXmlAttribute(node: XmlLexNode, localName: string, value: string): XmlReplacement {
  const attribute = node.attributes.find((item) => item.localName === localName);
  if (attribute) return { start: attribute.valueStart, end: attribute.valueEnd, value: escapeXmlAttribute(value), label: `${node.name}@${localName}` };
  // Insert before the lexical closing delimiter. Existing whitespace and quote
  // style elsewhere in the element are left untouched.
  const insertion = node.selfClosing ? node.startTagEnd - 2 : node.startTagEnd - 1;
  return { start: insertion, end: insertion, value: ` ${localName}="${escapeXmlAttribute(value)}"`, label: `${node.name}@${localName}` };
}

function renderTokenizedXml(
  value: string,
  sourceTemplate: Element,
  lexicalSource: XmlLexNode,
  targetTemplate: Element,
  lexicalTarget: XmlLexNode,
  original: string,
) {
  const sourceElements = Array.from(sourceTemplate.children);
  const targetElements = Array.from(targetTemplate.children);
  const useSource = sourceElements.length > 0;
  const templates = useSource ? sourceElements : targetElements;
  const lexicalTemplates = (useSource ? lexicalSource : lexicalTarget).children;
  if (templates.length !== lexicalTemplates.length) throw new Error("The protected bilingual tag map is ambiguous; export was stopped safely.");
  const openings = [...value.matchAll(TOKEN_RE)];
  if (!openings.length) return escapeXmlText(value.replace(CLOSE_TOKEN_RE, ""));
  let output = escapeXmlText(value.slice(0, openings[0].index).replace(CLOSE_TOKEN_RE, ""));
  for (let index = 0; index < openings.length; index += 1) {
    const opening = openings[index];
    const tokenId = Number(opening[1]);
    const template = templates[tokenId];
    const lexical = lexicalTemplates[tokenId];
    if (!template || !lexical) throw new Error("A protected SDLXLIFF/XLIFF tag is missing or duplicated.");
    const nextStart = openings[index + 1]?.index ?? value.length;
    const afterOpen = (opening.index ?? 0) + opening[0].length;
    const slice = value.slice(afterOpen, nextStart);
    const close = new RegExp(`⟦\\/T${tokenId}⟧`, "u").exec(slice);
    let tail = slice;
    if (close) {
      const inner = slice.slice(0, close.index);
      if (template.children.length && inner !== template.textContent) throw new Error("Nested inline tag text must be edited in Trados Studio.");
      if (lexical.selfClosing && inner) throw new Error("Text cannot be placed inside a standalone protected tag.");
      output += lexical.selfClosing || template.children.length
        ? original.slice(lexical.start, lexical.end)
        : original.slice(lexical.start, lexical.contentStart) + escapeXmlText(inner) + original.slice(lexical.contentEnd, lexical.end);
      tail = slice.slice(close.index + close[0].length);
    } else {
      output += original.slice(lexical.start, lexical.end);
    }
    output += escapeXmlText(tail.replace(CLOSE_TOKEN_RE, ""));
  }
  return output;
}

function applyXmlReplacements(original: string, replacements: XmlReplacement[]) {
  const ordered = [...replacements].sort((left, right) => left.start - right.start || left.end - right.end);
  for (let index = 0; index < ordered.length; index += 1) {
    const replacement = ordered[index];
    if (replacement.start < 0 || replacement.end < replacement.start || replacement.end > original.length) {
      throw new Error(`Unsafe XML range for ${replacement.label}; export was stopped.`);
    }
    const previous = ordered[index - 1];
    if (previous && replacement.start < previous.end) throw new Error(`Overlapping XML edits (${previous.label}, ${replacement.label}); export was stopped.`);
  }
  let output = original;
  for (const replacement of ordered.reverse()) {
    output = output.slice(0, replacement.start) + replacement.value + output.slice(replacement.end);
  }
  return output;
}

function tokenizedContent(element: Element): string {
  let result = "";
  for (const node of Array.from(element.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) result += node.nodeValue ?? "";
    else if (node.nodeType === Node.ELEMENT_NODE) {
      const index = Array.from(element.children).indexOf(node as Element);
      result += `⟦T${index}⟧`;
      const text = (node as Element).textContent ?? "";
      if (text) result += `${text}⟦/T${index}⟧`;
    }
  }
  return result;
}

function elements(root: ParentNode, localName: string): Element[] {
  return Array.from((root as Document | Element).getElementsByTagNameNS("*", localName));
}

function directChild(root: Element, localName: string): Element | null {
  return Array.from(root.children).find((item) => item.localName === localName) ?? null;
}

function confirmationFor(status: string) {
  if (status === "signed_off") return "ApprovedSignOff";
  if (status === "approved") return "ApprovedTranslation";
  if (status === "validated") return "Translated";
  return "Draft";
}

function checklistMatcher(term: Element): ((value: string) => boolean) | null {
  let expression = term.textContent ?? "";
  const negative = expression.startsWith("-");
  if (negative) expression = expression.slice(1);
  const mode = (term.getAttribute("searchmode") || "simple").toLocaleLowerCase();
  const sensitive = term.getAttribute("casesensitive") === "yes";
  let matcher: (value: string) => boolean;
  if (mode === "simple") {
    if (!expression) return null;
    matcher = sensitive
      ? (value) => value.includes(expression)
      : (value) => value.toLocaleLowerCase().includes(expression.toLocaleLowerCase());
  } else if (mode === "regexp") {
    try {
      const normalized = expression
        .replace(/^"|"$/g, "")
        .replaceAll("[:num:]", "\\d")
        .replaceAll("[:space:]", "\\s")
        .replaceAll("[:alpha:]", "[A-Za-zÀ-ÖØ-öø-ÿ]")
        .replaceAll("[:alphanum:]", "[0-9A-Za-zÀ-ÖØ-öø-ÿ]")
        .replace(/(?<!\\)>/g, "\\b")
        .replace(/(?<!\\)</g, "\\b");
      const regex = new RegExp(normalized, sensitive ? "u" : "iu");
      matcher = (value) => regex.test(value);
    } catch {
      return null;
    }
  } else {
    return null;
  }
  return negative ? (value) => !matcher(value) : matcher;
}

function base64Url(value: Uint8Array) {
  return btoa(String.fromCharCode(...value)).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function fromBase64Url(value: string) {
  return fromBase64(value.replaceAll("-", "+").replaceAll("_", "/"));
}

function fromBase64(value: string) {
  const padded = value + "=".repeat((4 - value.length % 4) % 4);
  const raw = atob(padded);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

/** Read the useful text layer of modern Word/PowerPoint references locally. */
export function extractPortableOfficeText(reference: MobileReferenceFile): string | null {
  const lower = reference.name.toLocaleLowerCase();
  if (!lower.endsWith(".docx") && !lower.endsWith(".pptx")) return null;
  try {
    const archive = unzipSync(fromBase64(reference.content_base64));
    if (lower.endsWith(".docx")) {
      const documentXml = archive["word/document.xml"];
      if (!documentXml) return null;
      return openXmlParagraphs(decoder.decode(documentXml));
    }
    const slides = Object.entries(archive)
      .filter(([name]) => /^ppt\/slides\/slide\d+\.xml$/i.test(name))
      .sort(([left], [right]) => Number(left.match(/slide(\d+)/i)?.[1] ?? 0) - Number(right.match(/slide(\d+)/i)?.[1] ?? 0));
    const output: string[] = [];
    for (let index = 0; index < slides.length; index += 1) {
      const text = openXmlParagraphs(decoder.decode(slides[index][1]));
      if (text) output.push(`Slide ${index + 1}\n${text}`);
    }
    return output.join("\n\n") || null;
  } catch {
    return null;
  }
}

/**
 * Decode a modern Word reference completely in the browser. Mammoth keeps the
 * useful Word semantics (headings, lists, tables, emphasis, links and embedded
 * images); DOMPurify makes the resulting document safe to mount in the app.
 * The original DOCX bytes are never uploaded or rewritten.
 */
export async function extractPortableDocxPreview(reference: MobileReferenceFile): Promise<PortableDocxPreview | null> {
  if (!reference.name.toLocaleLowerCase().endsWith(".docx")) return null;
  try {
    const [{ default: mammoth }, { default: DOMPurify }] = await Promise.all([import("mammoth"), import("dompurify")]);
    const bytes = fromBase64(reference.content_base64);
    const arrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    // Vite selects Mammoth's browser unzipper (arrayBuffer); Vitest/SSR selects
    // its Node unzipper (buffer). Supplying both keeps the decoder identical in
    // production and integrity tests without introducing a server dependency.
    const mammothInput = { arrayBuffer, buffer: bytes } as unknown as Parameters<typeof mammoth.convertToHtml>[0];
    const result = await mammoth.convertToHtml(mammothInput, {
      convertImage: mammoth.images.dataUri,
      includeDefaultStyleMap: true,
      includeEmbeddedStyleMap: true,
      styleMap: [
        "p[style-name='Title'] => h1:fresh",
        "p[style-name='Subtitle'] => p.docx-subtitle:fresh",
        "p[style-name='Heading 1'] => h2:fresh",
        "p[style-name='Heading 2'] => h3:fresh",
        "p[style-name='Heading 3'] => h4:fresh",
        "p[style-name='Quote'] => blockquote:fresh",
        "p[style-name='Caption'] => figcaption:fresh",
      ],
    });
    const html = DOMPurify.sanitize(result.value, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "style", "form", "input", "button", "textarea", "select", "iframe", "object", "embed"],
      FORBID_ATTR: ["style", "srcset"],
    });
    const text = extractPortableOfficeText(reference) ?? new DOMParser().parseFromString(html, "text/html").body.textContent?.trim() ?? "";
    if (!html.trim() && !text.trim()) return null;
    return {
      html,
      text,
      warnings: result.messages.map((message) => message.message).filter(Boolean),
    };
  } catch {
    return null;
  }
}

/**
 * Render an OpenDocument Text reference without sending it to a conversion
 * service. ODT is an XML package, so the phone can keep its headings, lists,
 * tables, emphasis, alignment, colours and embedded raster images while also
 * exposing a searchable text layer. Unsupported page-layout details can still
 * be compared with the staged PDF produced by the Mac-side job builder.
 */
export async function extractPortableOdtPreview(reference: MobileReferenceFile): Promise<PortableDocxPreview | null> {
  if (!reference.name.toLocaleLowerCase().endsWith(".odt")) return null;
  try {
    const [{ default: DOMPurify }] = await Promise.all([import("dompurify")]);
    const archive = unzipSync(fromBase64(reference.content_base64));
    const contentXml = archive["content.xml"];
    if (!contentXml) return null;
    const content = new DOMParser().parseFromString(decoder.decode(contentXml), "application/xml");
    if (content.querySelector("parsererror")) return null;
    const styles = portableOdtStyles(content);
    const body = [...content.getElementsByTagName("*")].find((item) => item.localName === "text" && item.parentElement?.localName === "body");
    if (!body) return null;
    const output = document.implementation.createHTMLDocument("");
    const host = output.createElement("article");
    host.className = "portable-odt-document";
    for (const child of Array.from(body.children)) {
      const converted = portableOdtNode(child, output, archive, styles);
      if (converted) host.append(converted);
    }
    const html = DOMPurify.sanitize(host.innerHTML, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "form", "input", "button", "textarea", "select", "iframe", "object", "embed"],
      FORBID_ATTR: ["srcset"],
    });
    const text = openDocumentText(decoder.decode(contentXml));
    return html.trim() || text.trim() ? { html, text, warnings: [] } : null;
  } catch {
    return null;
  }
}

function portableOdtStyles(documentNode: Document) {
  const result = new Map<string, string>();
  for (const style of [...documentNode.getElementsByTagName("*")].filter((item) => item.localName === "style")) {
    const name = odtAttribute(style, "name");
    if (!name) continue;
    const declarations: string[] = [];
    const properties = Array.from(style.children);
    for (const property of properties) {
      const weight = odtAttribute(property, "font-weight");
      const posture = odtAttribute(property, "font-style");
      const underline = odtAttribute(property, "text-underline-style");
      const strike = odtAttribute(property, "text-line-through-style");
      const color = safePortableCssColor(odtAttribute(property, "color"));
      const background = safePortableCssColor(odtAttribute(property, "background-color"));
      const size = safePortableCssLength(odtAttribute(property, "font-size"));
      const alignment = odtAttribute(property, "text-align");
      if (weight && /^(?:bold|[5-9]00)$/iu.test(weight)) declarations.push("font-weight:700");
      if (posture && /italic|oblique/iu.test(posture)) declarations.push("font-style:italic");
      if (underline && underline !== "none") declarations.push("text-decoration-line:underline");
      if (strike && strike !== "none") declarations.push("text-decoration-line:line-through");
      if (color) declarations.push(`color:${color}`);
      if (background && background !== "transparent") declarations.push(`background-color:${background}`);
      if (size) declarations.push(`font-size:${size}`);
      if (alignment && /^(?:left|right|center|justify|start|end)$/u.test(alignment)) declarations.push(`text-align:${alignment}`);
    }
    if (declarations.length) result.set(name, [...new Set(declarations)].join(";"));
  }
  return result;
}

function portableOdtNode(node: Element, output: Document, archive: Record<string, Uint8Array>, styles: Map<string, string>): Node | null {
  const local = node.localName;
  let element: HTMLElement | null = null;
  if (local === "h") {
    const level = Math.max(1, Math.min(6, Number(odtAttribute(node, "outline-level")) || 2));
    element = output.createElement(`h${level}`);
  } else if (local === "p") element = output.createElement("p");
  else if (local === "span") element = output.createElement("span");
  else if (local === "list") element = output.createElement(/number|ordered/iu.test(odtAttribute(node, "style-name")) ? "ol" : "ul");
  else if (local === "list-item") element = output.createElement("li");
  else if (local === "table") element = output.createElement("table");
  else if (local === "table-row") element = output.createElement("tr");
  else if (local === "table-cell" || local === "covered-table-cell") {
    if (local === "covered-table-cell") return null;
    element = output.createElement("td");
    const columnSpan = Number(odtAttribute(node, "number-columns-spanned"));
    const rowSpan = Number(odtAttribute(node, "number-rows-spanned"));
    if (columnSpan > 1) element.setAttribute("colspan", String(columnSpan));
    if (rowSpan > 1) element.setAttribute("rowspan", String(rowSpan));
  } else if (local === "a") {
    element = output.createElement("a");
    const href = odtAttribute(node, "href");
    if (/^https?:\/\//iu.test(href)) { element.setAttribute("href", href); element.setAttribute("target", "_blank"); element.setAttribute("rel", "noopener noreferrer"); }
  } else if (local === "line-break") return output.createElement("br");
  else if (local === "tab") return output.createTextNode("\t");
  else if (local === "s") return output.createTextNode(" ".repeat(Math.max(1, Math.min(100, Number(odtAttribute(node, "c")) || 1))));
  else if (local === "image") {
    const path = odtAttribute(node, "href").replace(/^\.\//u, "");
    const bytes = archive[path];
    if (!bytes || !/\.(?:png|jpe?g|gif|webp|svg)$/iu.test(path)) return null;
    const image = output.createElement("img");
    image.alt = path.split("/").at(-1) ?? "Embedded document image";
    image.src = `data:${portableMimeType(path)};base64,${bytesToBase64(bytes)}`;
    return image;
  } else if (["frame", "section", "text-box", "annotation", "soft-page-break"].includes(local)) element = output.createElement(local === "annotation" ? "aside" : "div");
  else {
    const fragment = output.createDocumentFragment();
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) fragment.append(output.createTextNode(child.nodeValue ?? ""));
      else if (child.nodeType === Node.ELEMENT_NODE) {
        const converted = portableOdtNode(child as Element, output, archive, styles);
        if (converted) fragment.append(converted);
      }
    }
    return fragment;
  }
  const style = styles.get(odtAttribute(node, "style-name"));
  if (style) element.setAttribute("style", style);
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) element.append(output.createTextNode(child.nodeValue ?? ""));
    else if (child.nodeType === Node.ELEMENT_NODE) {
      const converted = portableOdtNode(child as Element, output, archive, styles);
      if (converted) element.append(converted);
    }
  }
  return element;
}

function odtAttribute(element: Element, localName: string) {
  return Array.from(element.attributes).find((attribute) => attribute.localName === localName)?.value ?? "";
}

function safePortableCssColor(value: string) {
  return /^(?:#[0-9a-f]{3,8}|rgba?\([0-9.,% ]+\)|transparent)$/iu.test(value) ? value : "";
}

function safePortableCssLength(value: string) {
  return /^\d+(?:\.\d+)?(?:pt|px|em|rem|%)$/iu.test(value) ? value : "";
}

/**
 * Build a readable, locally searchable text view for references that Safari
 * cannot reliably preview itself. Workbook coordinates and labels are kept in
 * the text so a match still leads the translator to the exact sheet and row.
 */
export async function extractPortableReferenceText(reference: MobileReferenceFile): Promise<string | null> {
  const lower = reference.name.toLocaleLowerCase();
  if (WORKBOOK_EXTENSIONS.some((extension) => lower.endsWith(extension))) {
    try {
      const rows = await readPortableWorkbookRows(reference);
      if (!rows.length) return null;
      const output: string[] = [];
      let previousSheet = "";
      for (const row of rows) {
        if (row.sheet !== previousSheet) {
          if (output.length) output.push("");
          output.push(`Sheet: ${row.sheet}`);
          previousSheet = row.sheet;
        }
        output.push(`Row ${row.rowNumber}`);
        for (const cell of row.cells) output.push(`${cell.column} [${cell.coordinate}]: ${cell.value}`);
        output.push("");
      }
      return output.join("\n").trim() || null;
    } catch {
      return null;
    }
  }
  if (/\.(?:txt|md)$/u.test(lower)) {
    try { return decoder.decode(fromBase64(reference.content_base64)).trim() || null; }
    catch { return null; }
  }
  if (/\.html?$/u.test(lower)) {
    try {
      const html = decoder.decode(fromBase64(reference.content_base64));
      return new DOMParser().parseFromString(html, "text/html").body.textContent?.trim() || null;
    } catch { return null; }
  }
  if (lower.endsWith(".odt") || lower.endsWith(".odp")) {
    try {
      const archive = unzipSync(fromBase64(reference.content_base64));
      const content = archive["content.xml"];
      return content ? openDocumentText(decoder.decode(content)) || null : null;
    } catch { return null; }
  }
  if (lower.endsWith(".rtf")) {
    try { return portableRtfText(decoder.decode(fromBase64(reference.content_base64))) || null; }
    catch { return null; }
  }
  return extractPortableOfficeText(reference);
}

function portableRtfText(value: string) {
  return value
    .replace(/\\u(-?\d+)\??/g, (_match, raw: string) => String.fromCharCode((Number(raw) + 65536) % 65536))
    .replace(/\\'([0-9a-f]{2})/gi, (_match, hex: string) => String.fromCharCode(Number.parseInt(hex, 16)))
    .replace(/\\(?:par|line)\b ?/g, "\n")
    .replace(/\\tab\b ?/g, "\t")
    .replace(/\\[a-z]+-?\d* ?/gi, "")
    .replace(/\\[{}\\]/g, (match) => match.slice(1))
    .replace(/[{}]/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function openDocumentText(xml: string) {
  const documentNode = new DOMParser().parseFromString(xml, "application/xml");
  if (documentNode.querySelector("parsererror")) return "";
  const blocks = [...documentNode.getElementsByTagName("*")].filter((item) => ["h", "p"].includes(item.localName));
  return blocks.map((block) => (block.textContent ?? "").replace(/\s+/gu, " ").trim()).filter(Boolean).join("\n");
}

export function portableLayoutPreview(references: MobileReferenceFile[], reference: MobileReferenceFile) {
  return references.find((candidate) => candidate.layout_preview_for === reference.id) ?? null;
}

function openXmlParagraphs(xml: string) {
  const documentNode = new DOMParser().parseFromString(xml, "application/xml");
  if (documentNode.querySelector("parsererror")) return "";
  const paragraphs = elements(documentNode, "p");
  const containers = paragraphs.length ? paragraphs : [documentNode.documentElement];
  return containers.map((paragraph) => {
    let result = "";
    const walker = documentNode.createTreeWalker(paragraph, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    let node: Node | null = walker.currentNode;
    while (node) {
      if (node.nodeType === Node.TEXT_NODE && node.parentElement?.localName === "t") result += node.nodeValue ?? "";
      else if (node.nodeType === Node.ELEMENT_NODE && (node as Element).localName === "tab") result += "\t";
      else if (node.nodeType === Node.ELEMENT_NODE && ["br", "cr"].includes((node as Element).localName)) result += "\n";
      node = walker.nextNode();
    }
    return result.trim();
  }).filter(Boolean).join("\n");
}

function applyLegacyBiffStyles(
  XLSX: typeof import("xlsx-js-style"),
  input: Uint8Array,
  groupRows: Set<number>,
): Uint8Array {
  const cfb = XLSX.CFB.read(input, { type: "array" }) as unknown as { FileIndex: Array<{ content?: unknown }> };
  const entry = XLSX.CFB.find(cfb as never, "Workbook") as unknown as { content?: unknown; size?: number } | null;
  if (!entry?.content) throw new Error("The legacy report workbook stream is unavailable.");
  // xlsx-js-style returns Buffer in Node, Uint8Array in most browsers, and an
  // array-like CFB payload in some Safari bundles. Copy into this realm before
  // constructing DataView so phone export behaves identically in all three.
  const stream = insertLegacyXfs(insertLegacyFonts(portableBytes(entry.content)));
  entry.content = stream;
  entry.size = stream.length;
  const view = new DataView(stream.buffer as ArrayBuffer, stream.byteOffset, stream.byteLength);
  let offset = 0;
  let xfIndex = 0;
  while (offset + 4 <= stream.length) {
    const recordId = view.getUint16(offset, true);
    const length = view.getUint16(offset + 2, true);
    const data = offset + 4;
    if (data + length > stream.length) throw new Error("The legacy report workbook stream is truncated.");
    if (recordId === 0x00e0 && length >= 20) {
      if (xfIndex <= 16) patchLegacyXf(view, data, xfIndex);
      xfIndex += 1;
    } else if ([0x00fd, 0x0201, 0x0203, 0x027e, 0x0205, 0x0006, 0x0204, 0x00d6].includes(recordId) && length >= 6) {
      const row = view.getUint16(data, true);
      const column = view.getUint16(data + 2, true);
      view.setUint16(data + 4, legacyStyleIndex(row, column, groupRows), true);
    } else if (recordId === 0x00bd && length >= 12) {
      const row = view.getUint16(data, true);
      const firstColumn = view.getUint16(data + 2, true);
      const lastColumn = view.getUint16(data + length - 2, true);
      for (let column = firstColumn; column <= lastColumn; column += 1) {
        const cellOffset = data + 4 + (column - firstColumn) * 6;
        if (cellOffset + 2 <= data + length - 2) view.setUint16(cellOffset, legacyStyleIndex(row, column, groupRows), true);
      }
    } else if (recordId === 0x00be && length >= 8) {
      const row = view.getUint16(data, true);
      const firstColumn = view.getUint16(data + 2, true);
      const lastColumn = view.getUint16(data + length - 2, true);
      for (let column = firstColumn; column <= lastColumn; column += 1) {
        const cellOffset = data + 4 + (column - firstColumn) * 2;
        if (cellOffset + 2 <= data + length - 2) view.setUint16(cellOffset, legacyStyleIndex(row, column, groupRows), true);
      }
    }
    offset = data + length;
  }
  const rewritten = XLSX.CFB.write(cfb as never, { type: "array" }) as unknown;
  return portableBytes(rewritten);
}

function portableBytes(value: unknown): Uint8Array {
  if (value instanceof ArrayBuffer) return new Uint8Array(value.slice(0));
  if (ArrayBuffer.isView(value)) {
    const view = value as ArrayBufferView;
    return new Uint8Array(view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer);
  }
  if (typeof value === "string") return Uint8Array.from(value, (character) => character.charCodeAt(0) & 0xff);
  if (Array.isArray(value) || (value && typeof value === "object" && "length" in value)) {
    return Uint8Array.from(value as ArrayLike<number>);
  }
  throw new Error("The legacy report workbook returned an unsupported binary stream.");
}

function legacyStyleIndex(row: number, column: number, groupRows: Set<number>) {
  if (row === 0 && column <= 2) return 1;
  if (row === 0 && column === 3) return 2;
  if (row === 1 && column === 3) return 3;
  if (row === 2 && column === 3) return 4;
  if (row === 4 && column <= 3) return 5;
  if (row >= 5 && row <= 8) {
    if (column === 0) return 6;
    if (column === 1) return 7;
    if (column === 2) return 8;
    if (column === 3) return 9;
    return 11;
  }
  if (row === 10) return column === 2 || column === 3 ? 10 : 11;
  if (groupRows.has(row)) return 12;
  if (row > 10) {
    if (column === 0) return 14;
    if (column === 1) return 15;
    if (column === 2 || column === 3) return 16;
    return 13;
  }
  return 11;
}

function patchLegacyXf(view: DataView, offset: number, style: number) {
  const templates = [
    [8, "2000310009002000000c0000000000000002c020"],
    [3, "100000000100100000180000000000000002c020"],
    [1, "1000000001002b00005800000000000000062920"],
    [2, "1000000001002b00005800000000000000062920"],
    [5, "1000000001002b00005800000000000000062920"],
    [2, "1000000001002000005800000000000000062c20"],
    [2, "1000000001000000007811114020402000062920"],
    [1, "1000310001002800007c01114000402000062920"],
    [1, "1000000001002800007800110000402000062920"],
    [1, "1000000001002800007810110020402000062920"],
    [2, "100000000100280000180000000000000002c020"],
    [2, "100000000100200000080000000000000002c020"],
    [2, "1000000001002000004800000000000000062b20"],
    [9, "1000310001002800001c000000000000000002c020"],
    [6, "100000000100200000080000000000000002c020"],
    [7, "1000310009002000000c000000000000000002c020"],
    [1, "1000310001002800005400000000000000062a20"],
  ] as const;
  const [fontIndex, template] = templates[style] ?? templates[11];
  for (let index = 0; index < 20; index += 1) view.setUint8(offset + index, Number.parseInt(template.slice(index * 2, index * 2 + 2), 16));
  view.setUint16(offset, fontIndex, true);
}

function insertLegacyFonts(input: Uint8Array): Uint8Array {
  const originalView = new DataView(input.buffer as ArrayBuffer, input.byteOffset, input.byteLength);
  let offset = 0;
  let insertionOffset = -1;
  const boundSheets: number[] = [];
  while (offset + 4 <= input.length) {
    const recordId = originalView.getUint16(offset, true);
    const length = originalView.getUint16(offset + 2, true);
    const end = offset + 4 + length;
    if (end > input.length) throw new Error("The legacy report workbook stream is truncated.");
    if (recordId === 0x0031) insertionOffset = end;
    if (recordId === 0x0085 && length >= 4) boundSheets.push(offset);
    offset = end;
  }
  if (insertionOffset < 0 || !boundSheets.length) throw new Error("The legacy report workbook font or sheet table is unavailable.");
  const fonts = [
    legacyFontRecord({}),
    legacyFontRecord({ bold: true }),
    legacyFontRecord({ height: 400 }),
    legacyFontRecord({ colour: 12 }),
    legacyFontRecord({ italic: true, colour: 14 }),
    legacyFontRecord({ italic: true, colour: 10 }),
    legacyFontRecord({ italic: true, colour: 12 }),
    legacyFontRecord({ italic: true, colour: 17 }),
  ];
  const addedLength = fonts.reduce((sum, value) => sum + value.length, 0);
  const expanded = new Uint8Array(input.length + addedLength);
  expanded.set(input.subarray(0, insertionOffset), 0);
  let cursor = insertionOffset;
  for (const font of fonts) {
    expanded.set(font, cursor);
    cursor += font.length;
  }
  expanded.set(input.subarray(insertionOffset), cursor);
  const view = new DataView(expanded.buffer as ArrayBuffer);
  for (const oldOffset of boundSheets) {
    const newOffset = oldOffset + (oldOffset >= insertionOffset ? addedLength : 0);
    view.setUint32(newOffset + 4, view.getUint32(newOffset + 4, true) + addedLength, true);
  }
  return expanded;
}

function insertLegacyXfs(input: Uint8Array): Uint8Array {
  const originalView = new DataView(input.buffer as ArrayBuffer, input.byteOffset, input.byteLength);
  let offset = 0;
  let insertionOffset = -1;
  let xfCount = 0;
  const boundSheets: number[] = [];
  while (offset + 4 <= input.length) {
    const recordId = originalView.getUint16(offset, true);
    const length = originalView.getUint16(offset + 2, true);
    const end = offset + 4 + length;
    if (end > input.length) throw new Error("The legacy report workbook stream is truncated.");
    if (recordId === 0x00e0) {
      insertionOffset = end;
      xfCount += 1;
    }
    if (recordId === 0x0085 && length >= 4) boundSheets.push(offset);
    offset = end;
  }
  if (xfCount >= 17) return input;
  if (insertionOffset < 0 || !boundSheets.length) throw new Error("The legacy report workbook style or sheet table is unavailable.");
  const missing = 17 - xfCount;
  const addedLength = missing * 24;
  const expanded = new Uint8Array(input.length + addedLength);
  expanded.set(input.subarray(0, insertionOffset), 0);
  let cursor = insertionOffset;
  for (let index = 0; index < missing; index += 1) {
    const record = new DataView(expanded.buffer, cursor, 24);
    record.setUint16(0, 0x00e0, true);
    record.setUint16(2, 20, true);
    cursor += 24;
  }
  expanded.set(input.subarray(insertionOffset), cursor);
  const view = new DataView(expanded.buffer as ArrayBuffer);
  for (const oldOffset of boundSheets) {
    const newOffset = oldOffset + (oldOffset >= insertionOffset ? addedLength : 0);
    view.setUint32(newOffset + 4, view.getUint32(newOffset + 4, true) + addedLength, true);
  }
  return expanded;
}

function legacyFontRecord(options: { height?: number; bold?: boolean; italic?: boolean; colour?: number } = {}) {
  const name = "Calibri";
  const dataLength = 16 + name.length * 2;
  const output = new Uint8Array(4 + dataLength);
  const view = new DataView(output.buffer);
  view.setUint16(0, 0x0031, true);
  view.setUint16(2, dataLength, true);
  view.setUint16(4, options.height ?? 220, true);
  view.setUint16(6, options.italic ? 0x0002 : 0, true);
  view.setUint16(8, options.colour ?? 8, true);
  view.setUint16(10, options.bold ? 700 : 400, true);
  view.setUint16(12, 0, true);
  view.setUint8(14, 0);
  view.setUint8(15, 2);
  view.setUint8(16, 0);
  view.setUint8(17, 0);
  view.setUint8(18, name.length);
  view.setUint8(19, 1);
  for (let index = 0; index < name.length; index += 1) view.setUint16(20 + index * 2, name.charCodeAt(index), true);
  return output;
}
